import { DomSanitizer } from '@angular/platform-browser';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { Editor } from '@tiptap/core';
import { DOMSerializer } from '@tiptap/pm/model';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import FontFamily from '@tiptap/extension-font-family';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { FontSize } from './extensions/font-size';
import { ParagraphIndent } from './extensions/paragraph-indent';
import { SecondarySlashMenu, SlashMenu, SlashSuggestionState } from './extensions/slash-menu';
import { InlineImage } from './extensions/inline-image';
import {
    MentionCardAttrs,
    mentionAttrsToTokenAttrs,
    tokenAttrsToMentionAttrs
} from './extensions/mention-card';
import {
    ReferenceToken,
    ReferenceTokenAttrs,
    extensionFromFileName,
    fileKindFromExtension,
    stripReferenceTokenMarks
} from './extensions/reference-token';
import { CommentAnchor } from './extensions/comment-anchor';
import {
    DEFAULT_MAX_UPLOAD_BYTES,
    FileDropDetail,
    FileDropHandler,
    FileDropRejectDetail,
    dataTransferHasFiles
} from './extensions/file-drop-handler';
import {
    FILE_DROP_HINT_INLINE,
    HostFileDropRefusal,
    HostFileDropSplit,
    TIPTAP_DEFAULT_FILE_DROP_MODE,
    TiptapFileDropMode,
    fileDropHint,
    handsFileDropToHost,
    splitHostFileDrop
} from './file-drop-mode';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { AppConsts } from '@shared/commonHelper/AppConsts';
import { ReducedMotionHelper } from '@shared/helpers/ReducedMotionHelper';
import { AttachmentServiceProxy, CommonAttachment } from '@shared/service-proxies/service-proxies';
import { DataService } from '@app/shared/common/auth/data.service';
import { ImageUploadResult } from '../components/image-upload-modal/image-upload-modal.component';
import {
    FontFamilyConfig,
    RteChangeEvent,
    RteCommand,
    RteCreatedEvent,
    RteKeyUpEvent,
    SlashMenuItem,
    SlashMenuItemSelectArgs,
    TiptapAnchorHoverEvent,
    TiptapNewCommentEvent
} from './tiptap-editor.types';

const FONT_SIZE_PRESETS = ['10px', '12px', '14px', '16px', '18px', '24px', '32px', '48px'];

/**
 * The marker of a comment's person chip, a link whose href is `#vfmention=<userId>` (CO-09). The editor shows no link popover
 * for it: the popover would print the raw fragment, and its Edit and Remove link buttons would let a chip be turned into a plain
 * link. KEEP IN STEP with `MENTION_FRAGMENT` in main/agenda/edit-task-modal/conversations/comment-mention.ts (shared code does
 * not import from a feature folder).
 */
const MENTION_LINK_FRAGMENT = '#vfmention=';

/** How long the people list must stay as it is before its count is announced, so typing does not announce every keystroke. */
const SLASH_STATUS_DELAY_MS = 350;

@Component({
    selector: 'vf-tiptap-editor',
    templateUrl: './tiptap-editor.component.html',
    styleUrls: ['./tiptap-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TiptapEditorComponent implements AfterViewInit, OnChanges, OnDestroy {

    @Input() value: string = '';
    @Input() placeholder: string = '';
    @Input() readonly: boolean = false;
    @Input() maxLength: number | null = null;
    @Input() height: string = '100%';
    @Input() fontFamily: FontFamilyConfig = { default: 'Inter' };
    @Input() editorId: string = '';
    /**
     * The accessible name of the editing area (CO-09): a screen reader says "{ariaLabel}, edit, multi line" when focus lands in
     * it. Empty = no name, as before. The comment composer passes "Write a comment".
     */
    @Input() ariaLabel: string = '';
    @Input() showCharCount: boolean = false;
    @Input() showBubbleMenu: boolean = true;

    /**
     * Opt-in responsive toolbar: as the row narrows, formatting buttons drop out in a fixed priority
     * order and reappear inside a `···` menu, so nothing becomes unreachable. OFF by default — the
     * comment composer, reply composer and AI overlay keep the plain wrapping toolbar they have today.
     *
     * Thresholds are the design's (Trovve.dc.html 6114–6121) and are measured against the space the
     * BUTTONS actually get, i.e. the row minus whatever fixed content is projected into the leading and
     * trailing slots.
     */
    @Input() collapsibleToolbar: boolean = false;

    /** Width available to the buttons, from the ResizeObserver below. 0 until first measure. */
    public toolbarButtonSpace: number = 0;
    public moreMenuOpen: boolean = false;

    /**
     * Width our always-inline controls consume before any collapsible tool gets a look in: Write-with-AI,
     * Proofread, the `···` and the separators. The design's thresholds are measured against ITS bar,
     * whose buttons are narrower, so they don't transfer raw. Calibrated against this toolbar rather
     * than hand-tuning nine numbers. (Was 380 while the four combo pickers were still inline; they now
     * live in the `···` permanently, which gives ~190px back.)
     */
    private static readonly TOOLBAR_FIXED_COST = 190;

    /**
     * Priority order - lower `min` survives longer. Mirrors the design's ntShow* thresholds, with ONE
     * departure: the design drops the checklist first (530), which on this toolbar meant the task-notes
     * bar never showed it at any real pane width. A checklist is a list, so it now collapses with the
     * other two rather than ahead of everything.
     */
    private static readonly TOOL_MIN: { [key: string]: number } = {
        bold: 120, italic: 120, underline: 120,
        bullet: 230, numbered: 270, checklist: 290, link: 300,
        indent: 380, strike: 460,
    };

    /** The same tools as menu rows, for whatever no longer fits inline. */
    private static readonly TOOL_DEFS: { key: string; title: string; icon: string; cmd: string; mark?: string }[] = [
        { key: 'bold', title: 'Bold', icon: 'bold', cmd: 'bold', mark: 'bold' },
        { key: 'italic', title: 'Italic', icon: 'italic', cmd: 'italic', mark: 'italic' },
        { key: 'underline', title: 'Underline', icon: 'underline', cmd: 'underline', mark: 'underline' },
        { key: 'strike', title: 'Strikethrough', icon: 'strikethrough', cmd: 'strikeThrough', mark: 'strike' },
        { key: 'bullet', title: 'Bullet list', icon: 'list', cmd: 'insertUnorderedList', mark: 'bulletList' },
        { key: 'numbered', title: 'Numbered list', icon: 'list-ordered', cmd: 'insertOrderedList', mark: 'orderedList' },
        { key: 'checklist', title: 'Checkbox list', icon: 'list-checks', cmd: 'insertTaskList', mark: 'taskList' },
        { key: 'indent', title: 'Increase indent', icon: 'indent-increase', cmd: 'indent' },
        { key: 'outdent', title: 'Decrease indent', icon: 'indent-decrease', cmd: 'outdent' },
        { key: 'link', title: 'Insert link', icon: 'link', cmd: '__link' },
    ];

    private _toolbarRo: ResizeObserver | null = null;

    /** True when this tool still fits inline. Always true when the toolbar isn't collapsible. */
    public tbShow(key: string): boolean {
        if (!this.collapsibleToolbar) {
            return true;
        }
        // indent/outdent travel together under one threshold.
        const lookup = key === 'outdent' ? 'indent' : key;
        const min = TiptapEditorComponent.TOOL_MIN[lookup];
        return min === undefined || this.toolbarButtonSpace >= min + TiptapEditorComponent.TOOLBAR_FIXED_COST;
    }

    /** Everything currently pushed out of the row — the `···` menu's contents. */
    public get overflowTools(): { key: string; title: string; icon: string; cmd: string; mark?: string }[] {
        if (!this.collapsibleToolbar) {
            return [];
        }
        return TiptapEditorComponent.TOOL_DEFS.filter(t => !this.tbShow(t.key));
    }

    public toggleMoreMenu(): void {
        this.moreMenuOpen = !this.moreMenuOpen;
        this.cdr.markForCheck();
    }

    public closeMoreMenu(): void {
        if (this.moreMenuOpen) {
            this.moreMenuOpen = false;
            this.cdr.markForCheck();
        }
    }

    /** Menu row click — `__link` reuses the toolbar's own link flow, everything else is a plain command. */
    public runOverflowTool(tool: { cmd: string }): void {
        this.closeMoreMenu();
        if (tool.cmd === '__link') {
            this.openLinkInputPopup('add', '');
            return;
        }
        this.toolbarAction(tool.cmd as any);
    }

    /**
     * Watch the toolbar and recompute how much room the buttons have.
     *
     * Measured as row width minus the FIXED content in the projection slots: a host may project a
     * flexible spacer, and counting that as occupied would report ~0 space and collapse everything.
     * Anything with `flex-grow > 0` is therefore skipped — and so is anything taken OUT of flow, which
     * consumes no row width no matter how wide it measures. (The notes project a full-width hint layer
     * that sits over the buttons; counted, it reserved the entire row and collapsed every tool into the
     * `···` menu.)
     */
    private _observeToolbar(): void {
        if (!this.collapsibleToolbar || this._toolbarRo || typeof ResizeObserver === 'undefined') {
            return;
        }
        const el = this.host.nativeElement.querySelector('.vf-tiptap-toolbar') as HTMLElement | null;
        if (!el) {
            return;
        }
        const measure = () => {
            let reserved = 0;
            // Projected content lands as DIRECT children of the toolbar, and each one's OWN width is what it
            // costs the row. Summing GRANDchildren instead under-counted every host that tags its controls
            // individually (`<button toolbar-trailing><lucide-icon></lucide-icon></button>` reserved 16px for
            // the icon, not the button's 32px) — so the row believed it had space it did not have and the
            // tools never collapsed. Hosts that wrap their controls in one tagged element measure the same
            // either way.
            Array.from(el.children).forEach((child: Element) => {
                const n = child as HTMLElement;
                if (!n.hasAttribute('toolbar-leading') && !n.hasAttribute('toolbar-trailing')) {
                    return;
                }
                const cs = getComputedStyle(n);
                // A full-width layer that grows to fill the row (the notes' hint) consumes no real width, and
                // an out-of-flow one consumes none either — counting them reserved the whole row.
                if (parseFloat(cs.flexGrow || '0') > 0) {
                    return;
                }
                if (cs.position === 'absolute' || cs.position === 'fixed' || cs.display === 'none') {
                    return;
                }
                reserved += n.offsetWidth;
            });
            const space = Math.max(0, el.clientWidth - reserved);
            if (Math.abs(space - this.toolbarButtonSpace) > 2) {
                this.ngZone.run(() => {
                    this.toolbarButtonSpace = space;
                    this.cdr.markForCheck();
                });
            }
        };
        this._toolbarRo = new ResizeObserver(() => measure());
        this._toolbarRo.observe(el);
        measure();
        // The editor's view can initialise before the surrounding panel has laid out — the first measure
        // then reads clientWidth 0 and collapses the whole toolbar. Re-measure once the browser has
        // painted, and again shortly after for hosts that animate their width in (the task modal slides).
        this.ngZone.runOutsideAngular(() => {
            requestAnimationFrame(() => measure());
            setTimeout(() => measure(), 300);
        });
    }
    @Input() showSlashMenu: boolean = false;
    /** Character that opens the slash menu. Defaults to '/'; set to '@' for mention pickers. */
    @Input() slashTriggerChar: string = '/';
    /** Allow spaces inside the slash query. Keep true for the '/' command menu (multi-word search);
     *  set FALSE for '@' mention menus so the popup closes once a name has been inserted. */
    @Input() slashAllowSpaces: boolean = true;
    /**
     * Opt-in SECOND trigger character, so one editor can offer `/` and `@` at once (the comment
     * composer: `@` mentions a teammate, `/` inserts a file). Null = single trigger, which is every
     * other editor. Both triggers drive the SAME popup — only one menu can be open at a time — so the
     * host just answers whichever query event fired and sets `slashMenuItems` as usual.
     */
    @Input() secondaryTriggerChar: string | null = null;
    /** Spaces inside the secondary query. Same rule as `slashAllowSpaces`: false for mention-style. */
    @Input() secondaryAllowSpaces: boolean = true;
    @Input() showToolbar: boolean = false;
    @Input() showProofread: boolean = false;

    /**
     * Opt-in: render a "New comment" action in the selection bubble (after Proofread, behind a strong
     * divider) for the Unified Task Detail — Conversations notes. Emits `newCommentAction` with the
     * selected text + range. Default false, so every existing editor is unchanged. See
     * TASK_CONVERSATIONS_PLAN.md §7.
     */
    @Input() showNewComment: boolean = false;
    /** Independent control for the toolbar "Write with AI ▾" dropdown. Defaults
     *  to true so existing surfaces keep both bubble Proofread and the toolbar
     *  dropdown. The AI writing overlay sets this to false so its inner editor
     *  doesn't re-expose the entry point that opened the overlay in the first
     *  place. The bubble-menu Proofread button continues to be driven by
     *  `showProofread` alone. */
    @Input() showWriteAiToolbar: boolean = true;
    /** When true, render a standalone "Proofread" button in the toolbar
     *  *instead of* the Write-with-AI dropdown — used by simpler surfaces
     *  (e.g. the step / checklist edit modal) that want only the proofread
     *  affordance and not the full Ask-AI flow. Independent from
     *  `showWriteAiToolbar`; the host typically sets `showWriteAiToolbar=false`
     *  + `showProofreadButton=true` together. */
    @Input() showProofreadButton: boolean = false;
    /** Project ID used to look up the default folder + project-linked folders for image uploads. */
    @Input() projectId: number | null = null;
    /** Task ID — when set, uploaded images are also linked to this task so they
     *  appear under the Files tab. Pass null/0 when the host doesn't have a
     *  saved task yet (e.g. the add-task modal); the host can then listen to
     *  (imageUploaded) and link the file after task creation. */
    @Input() taskId: number | null = null;
    /** Disable the paste/drop file-upload flow on this editor instance.
     *  (Name kept for host compatibility — it now covers all file types, not
     *  just images.) */
    @Input() disableImageUpload: boolean = false;
    /**
     * What a pasted or dropped file does (file-drop-mode.ts): `'inline'` (the default) runs the upload modal and inserts a
     * reference token, as always; `'host'` hands the validated file to the host through `(fileDrop)` and inserts nothing (the
     * Global Conversations overlay's comment composers, which attach it as a chip). Read at drop time.
     */
    @Input() fileDropMode: TiptapFileDropMode = TIPTAP_DEFAULT_FILE_DROP_MODE;
    /** Callbacks invoked when a rendered mention chip is clicked. The host
     *  uses these to route to the lightbox / edit-task-modal / edit-company-modal. */
    @Input() mentionCardCallbacks: {
        onFileClick?: (attrs: MentionCardAttrs) => void;
        onFileOpenInFolder?: (attrs: MentionCardAttrs) => void;
        onFileOpenInBrowser?: (attrs: MentionCardAttrs) => void;
        onTaskClick?: (attrs: MentionCardAttrs) => void;
        onClientClick?: (attrs: MentionCardAttrs) => void;
    } | null = null;
    /** When true, the slash dropdown renders the grouped layout (Files / Tasks /
     *  Clients) with the header bar + keyboard hints + breadcrumb subtitles.
     *  Defaults to false so existing hosts keep the legacy flat list. */
    @Input() useGroupedSlashLayout: boolean = false;

    @Output() valueChange: EventEmitter<RteChangeEvent> = new EventEmitter<RteChangeEvent>();
    @Output() created: EventEmitter<RteCreatedEvent> = new EventEmitter<RteCreatedEvent>();
    @Output() keyUp: EventEmitter<RteKeyUpEvent> = new EventEmitter<RteKeyUpEvent>();
    @Output() slashMenuItemSelect: EventEmitter<SlashMenuItemSelectArgs> = new EventEmitter<SlashMenuItemSelectArgs>();
    @Output() slashQueryChange: EventEmitter<string> = new EventEmitter<string>();
    /** Same pair as above but for `secondaryTriggerChar`, so the host can tell the two menus apart. */
    @Output() secondaryMenuItemSelect: EventEmitter<SlashMenuItemSelectArgs> = new EventEmitter<SlashMenuItemSelectArgs>();
    @Output() secondaryQueryChange: EventEmitter<string> = new EventEmitter<string>();
    @Output() proofreadAction: EventEmitter<void> = new EventEmitter<void>();

    /** Fired by the bubble's "New comment" button (opt-in via showNewComment) with the current selection. */
    @Output() newCommentAction: EventEmitter<TiptapNewCommentEvent> = new EventEmitter<TiptapNewCommentEvent>();
    /** Fires as the pointer enters/leaves an anchor highlight in the notes (opt-in via showNewComment): the
     *  anchor id + hovered span (for hover-UI positioning), or null on leave. Drives the reverse
     *  highlight→stream-entry hover link and the anchored-comment hover callout. */
    @Output() anchorHover: EventEmitter<TiptapAnchorHoverEvent | null> = new EventEmitter<TiptapAnchorHoverEvent | null>();
    /** Fires when an anchor highlight is CLICKED (opt-in via showNewComment) — the host scrolls the stream
     *  to the matching comment and flashes it (spec §5). The click still places the caret normally. */
    @Output() anchorClick: EventEmitter<string> = new EventEmitter<string>();
    /** Fires when the user picks "Ask AI to write something" from the Write-with-AI
     *  dropdown. The host opens its Ask-AI callout in response. Independent from
     *  `proofreadAction`, which is fired by the dropdown's Proofread option and by
     *  the bubble-toolbar Proofread when a selection exists. */
    @Output() askAiAction: EventEmitter<void> = new EventEmitter<void>();
    /** Pill "Accept all" was clicked. Editor state is already cleared — the
     *  host listens to persist whatever HTML is currently in the editor. */
    @Output() proofreadReviewAccept: EventEmitter<void> = new EventEmitter<void>();
    /** Pill "Undo all" was clicked. Editor has already restored the snapshot
     *  via setValue + fired a `(valueChange)` for the rollback — the host
     *  listens here if it needs to do anything beyond the value handler. */
    @Output() proofreadReviewUndo: EventEmitter<void> = new EventEmitter<void>();
    /** Fires after an image upload completes (regardless of "copy link" toggle). */
    @Output() imageUploaded: EventEmitter<ImageUploadResult> = new EventEmitter<ImageUploadResult>();
    /**
     * `fileDropMode` 'host' only: EVERY file of one paste or drop on the editor (its text, its chrome, or a host's chrome through
     * `ingestExternalFiles`), already validated (not empty, within the size cap; a toast per refused one) and, for a pasted image,
     * given its unique name. Never empty. Nothing is uploaded or inserted by the editor; the host decides.
     */
    @Output() fileDrop: EventEmitter<File[]> = new EventEmitter<File[]>();

    @ViewChild('editorHost', { static: true }) editorHost!: ElementRef<HTMLDivElement>;
    @ViewChild('bubbleMenu', { static: false }) bubbleMenuEl!: ElementRef<HTMLDivElement>;
    @ViewChild('slashPopup', { static: true }) slashPopup!: ElementRef<HTMLDivElement>;
    @ViewChild('linkPopover', { static: false }) linkPopoverEl!: ElementRef<HTMLDivElement>;
    @ViewChild('linkInputField', { static: false }) linkInputField!: ElementRef<HTMLInputElement>;

    public editor: Editor | null = null;
    public charCount: number = 0;
    public fontSizePresets = FONT_SIZE_PRESETS;

    public bubbleVisible = false;
    public bubbleStyle: any = { display: 'none' };

    /** Hands every editor its own listbox id: a page can hold several editors, and the popup is moved into <body>. */
    private static _slashListSeq = 0;
    /**
     * The id of the grouped picker's listbox, and the stem of its options' ids (CO-09). The editing area points at the listbox
     * with `aria-controls` and at the highlighted option with `aria-activedescendant`, so a screen reader announces the option
     * while focus stays in the text.
     */
    public readonly slashListId: string = 'vf-tiptap-slash-' + ++TiptapEditorComponent._slashListSeq;
    /**
     * What the polite live region says about the people list ("3 people match"); '' = nothing. It lives outside the popup, which is
     * display:none while the list is empty, so "0 people match" is still spoken.
     */
    public slashStatusText = '';
    private _slashStatusTimer: number | null = null;
    /**
     * True while the people list has been answered with NO match for a typed query: the popup is then shown with its own empty text
     * ("No matches for ..."), where an empty list is otherwise hidden outright (`vf-tiptap-slash--empty`). Only after the host has
     * answered (see _slashAnswered), so a lookup still in flight never shows "no matches".
     */
    public slashShowsNoMatch = false;
    /** The host has answered the current query: it set `slashMenuItems` since the query last changed. Reset by every new query. */
    private _slashAnswered = false;

    public slashVisible = false;
    public slashItems: SlashMenuItem[] = [];
    public slashSelectedIndex = 0;
    public slashStyle: any = { display: 'none' };
    /** Current slash query (the text typed after the trigger char). Used by
     *  the grouped layout to render `Results for "{query}"` in the header. */
    public slashQuery: string = '';

    /** Empty-query header for the grouped popup. When one editor drives two triggers (the comment
     *  composer runs `@` people + `/` files through the same popup), the secondary trigger gets its
     *  own label so the header never reads "Recent files" over a list of teammates. */
    @Input() groupedPrimaryLabel = 'Recent files';
    @Input() groupedSecondaryLabel = 'Recent files';

    public get groupedHeaderLabel(): string {
        return this._secondaryMenuActive ? this.groupedSecondaryLabel : this.groupedPrimaryLabel;
    }

    public showFontColorPicker = false;
    public showHighlightPicker = false;
    public showFontSizeMenu = false;
    public showBubbleTableDeleteMenu = false;

    public showTbColorPicker = false;
    public showTbHighlightPicker = false;
    public showTbFontSizeMenu = false;
    public showTbTableMenu = false;
    public showTbWriteAiMenu = false;

    /**
     * Viewport coordinates for the Write-with-AI menu. The menu used to be `position: absolute` inside
     * the toolbar, which meant any scrolling ancestor clipped it -- on a task whose notes are empty the
     * notes region is only a couple of rows tall, so the menu was cut off by the section below it. It is
     * positioned against the viewport instead and cannot be clipped by an ancestor's overflow.
     */
    public tbWriteAiMenuStyle: { [key: string]: string } = {};

    /** Drives the floating "X change to review · Accept all · Undo all" pill
     *  that appears after an inline Proofread has been applied. The host calls
     *  showProofreadReview() to enter review mode and the pill's buttons emit
     *  acceptProofread / undoProofread events the host listens to. */
    public proofreadReviewVisible: boolean = false;
    public proofreadReviewLabel: string = '1 change to review';
    /** Show the in-progress "Proofreading…" pill while the AI request is in
     *  flight. The host calls showProofreadProgress() before issuing the call
     *  and either showProofreadReview() (success) or hideProofreadProgress()
     *  (error) when it returns. */
    public proofreadingInProgress: boolean = false;
    private _proofreadBeforeHtml: string | null = null;

    public linkPopoverVisible = false;
    public linkPopoverStyle: any = { display: 'none' };
    public currentLinkHref: string = '';

    public showLinkInputPopup = false;
    public linkInputUrl: string = '';
    public linkInputMode: 'add' | 'edit' = 'add';

    /** The drop card's second line for `fileDropMode` (file-drop-mode.ts), set in ngOnChanges so the template only reads it. */
    public fileDropHintText: string = FILE_DROP_HINT_INLINE;

    // --- Image upload + lightbox state ---
    public showImageUploadModal: boolean = false;
    public pendingImageFile: File | null = null;
    private _pendingInsertPos: number | null = null;

    public showLightbox: boolean = false;
    public lightboxImageUrl: string = '';
    public lightboxFileName: string = '';
    public lightboxFileSize: number = 0;
    /** Passed through so the lightbox can degrade to its "Open in new tab" card
     *  rather than an empty stage when the image bytes can't be fetched. */
    public lightboxExtension: string = '';
    public lightboxWebUrl: string = '';
    /** True while the OneDrive proxy fetch is in flight. */
    public lightboxLoading: boolean = false;

    /** Cache of in-flight or resolved blob-URL promises, keyed by `driveId|fileId`,
     *  so repeated renders (the same image in a list, or after a re-mount) don't
     *  re-download the bytes. URLs are revoked in ngOnDestroy. */
    private _imageUrlCache: Map<string, Promise<string>> = new Map<string, Promise<string>>();
    private _ownedImageBlobUrls: string[] = [];

    private _slashItemsBuffer: SlashMenuItem[] = [];
    private _suppressEmit = false;
    private _lastEmittedValue = '';
    private _isFullScreen = false;
    // Range of the trigger char + query in the doc (from SlashMenu state).
    // Captured so click-to-select can delete it before the host inserts the mention.
    private _currentSlashRange: { from: number; to: number } | null = null;

    // Matches the project's standard toastr config (AppComponentBase.toastrOptions)
    // so the CustomToastComponent renders correctly.
    private _toastrOptions: any = {
        closeButton: false,
        debug: false,
        newestOnTop: false,
        progressBar: false,
        toastClass: 'toast',
        positionClass: 'toast-bottom-full-width',
        preventDuplicates: true,
        onclick: null,
        showDuration: '5000',
        hideDuration: '1000',
        timeOut: '5000',
        extendedTimeOut: '1000',
        showEasing: 'swing',
        hideEasing: 'linear',
        showMethod: 'fadeIn',
        hideMethod: 'fadeOut',
        tapToDismiss: false,
        enableHtml: true,
        maxOpened: 1
    };

    // 10-column palette: column = hue, row = shade (dark → light)
    public textColorPalette: string[] = [
        // Row 1 — neutrals + bold primaries (darkest)
        '#000000', '#1f2937', '#7f1d1d', '#7c2d12', '#713f12', '#14532d', '#155e75', '#1e3a8a', '#581c87', '#831843',
        // Row 2
        '#374151', '#4b5563', '#991b1b', '#9a3412', '#854d0e', '#166534', '#0e7490', '#1e40af', '#6b21a8', '#9d174d',
        // Row 3
        '#6b7280', '#9ca3af', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0891b2', '#2563eb', '#9333ea', '#db2777',
        // Row 4 — base hues
        '#9ca3af', '#d1d5db', '#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#a855f7', '#ec4899',
        // Row 5
        '#d1d5db', '#e5e7eb', '#fca5a5', '#fdba74', '#fde047', '#86efac', '#67e8f9', '#93c5fd', '#d8b4fe', '#f9a8d4',
        // Row 6
        '#f3f4f6', '#f9fafb', '#fee2e2', '#ffedd5', '#fef9c3', '#dcfce7', '#cffafe', '#dbeafe', '#f3e8ff', '#fce7f3',
        // Row 7 — lightest
        '#ffffff', '#fafafa', '#fef2f2', '#fff7ed', '#fefce8', '#f0fdf4', '#ecfeff', '#eff6ff', '#faf5ff', '#fdf2f8'
    ];

    // Highlight: same column structure but starts with a "no highlight" cell
    public highlightPalette: Array<string | null> = [
        // Row 1 (first cell = no highlight, then bold)
        null, '#000000', '#7f1d1d', '#7c2d12', '#713f12', '#14532d', '#155e75', '#1e3a8a', '#581c87', '#831843',
        '#374151', '#4b5563', '#991b1b', '#9a3412', '#854d0e', '#166534', '#0e7490', '#1e40af', '#6b21a8', '#9d174d',
        '#6b7280', '#9ca3af', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0891b2', '#2563eb', '#9333ea', '#db2777',
        '#9ca3af', '#d1d5db', '#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#a855f7', '#ec4899',
        '#d1d5db', '#e5e7eb', '#fca5a5', '#fdba74', '#fde047', '#86efac', '#67e8f9', '#93c5fd', '#d8b4fe', '#f9a8d4',
        '#f3f4f6', '#f9fafb', '#fee2e2', '#ffedd5', '#fef9c3', '#dcfce7', '#cffafe', '#dbeafe', '#f3e8ff', '#fce7f3'
    ];

    // Legacy aliases (template still references them)
    public get colorSwatches(): string[] { return this.textColorPalette; }
    public get highlightSwatches(): Array<string | null> { return this.highlightPalette; }

    constructor(
        private host: ElementRef<HTMLElement>,
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone,
        private _toastr: ToastrService,
        private _http: HttpClient,
        private _attachmentService: AttachmentServiceProxy,
        private _dataService: DataService,
        private _sanitizer: DomSanitizer
    ) {}

    /**
     * Mark each row's icon markup trusted so `[innerHTML]` actually paints it.
     *
     * Angular's HTML sanitizer strips `<svg>`, so the picker was rendering an empty icon slot on EVERY row
     * — a file, a task, a person and a company were indistinguishable until you read the text. The markup is
     * built in app code (`rowIconSvgForRow` / `rowIconSvg`), never from user input or the server, so
     * trusting it introduces no new surface; the alternative is a hard-coded icon set in this template,
     * which would stop hosts from choosing their own glyphs.
     */
    private _trustRowIcons(items: SlashMenuItem[]): SlashMenuItem[] {
        (items || []).forEach((item: SlashMenuItem, index: number) => {
            if (item && item.rowIconSvg && !item.rowIconSafe) {
                item.rowIconSafe = this._sanitizer.bypassSecurityTrustHtml(item.rowIconSvg);
            }
            // The id of the row's option element (CO-09), by position, so `aria-activedescendant` can name it.
            if (item) {
                item['optionId'] = this.slashListId + '-opt-' + index;
            }
        });
        return items || [];
    }

    ngAfterViewInit(): void {
        // Responsive toolbar measurement (no-op unless [collapsibleToolbar] is on).
        this._observeToolbar();
        // Re-parent the slash popup to <body> so its position: fixed coords
        // resolve against the viewport rather than a transformed ancestor
        // (Bootstrap modal dialogs apply transforms that capture fixed children).
        if (this.slashPopup?.nativeElement) {
            document.body.appendChild(this.slashPopup.nativeElement);
        }
        // Same treatment for the selection bubble, and for the same reason: its `position: fixed` coords
        // must resolve against the viewport, and it must not be clipped by the editor's scrolling pane.
        if (this.bubbleMenuEl?.nativeElement) {
            document.body.appendChild(this.bubbleMenuEl.nativeElement);
        }
        // Document-level mousedown so a click outside both the editor host
        // and the slash popup dismisses the slash menu. The Suggestion
        // plugin terminates on editor blur in some cases but not when the
        // user clicks a non-focusable element (e.g. an empty area of the
        // page), so we handle it explicitly here.
        document.addEventListener('mousedown', this._onDocumentMouseDown, true);
        this.ngZone.runOutsideAngular(() => {
            this.editor = new Editor({
                element: this.editorHost.nativeElement,
                content: this.value || '',
                editable: !this.readonly,
                editorProps: { attributes: this._editorAriaAttributes() },
                extensions: this._buildExtensions(),
                onUpdate: ({ editor }) => {
                    const html = editor.getHTML();
                    this.charCount = editor.storage.characterCount?.characters() ?? 0;
                    if (this._suppressEmit) {
                        return;
                    }
                    // Typing after a Proofread retires the review pill — the user has moved on, and
                    // "Undo all" would now clobber what they just wrote. Placed AFTER the suppress
                    // guard on purpose: apply/accept/undo all go through suppressed programmatic
                    // writes, so only a genuine human edit dismisses it.
                    this._dismissProofreadReviewOnEdit();
                    this._lastEmittedValue = html;
                    this.ngZone.run(() => {
                        this.valueChange.emit({ value: html, bubbles: false });
                        this.cdr.markForCheck();
                    });
                },
                onCreate: ({ editor }) => {
                    // Content passed to `new Editor({ content })` never fires a
                    // transaction, so the token extension's appendTransaction can't
                    // see it. Clean the initial doc explicitly.
                    stripReferenceTokenMarks(editor);
                    this.charCount = editor.storage.characterCount?.characters() ?? 0;
                    this.ngZone.run(() => {
                        this.created.emit({ editor });
                        this.cdr.markForCheck();
                    });
                },
                onSelectionUpdate: () => {
                    this._updateBubbleMenuPosition();
                    this._updateLinkPopover();
                },
                onTransaction: () => {
                    this.charCount = this.editor?.storage.characterCount?.characters() ?? 0;
                }
            });

            const dom = this.editor.view.dom as HTMLElement;
            dom.addEventListener('keyup', this._onDomKeyUp);
            dom.addEventListener('blur', this._onDomBlur);
            dom.addEventListener('mouseover', this._onDomAnchorHover);
            dom.addEventListener('mouseleave', this._onDomAnchorLeave);
            dom.addEventListener('click', this._onDomAnchorClick);

            if (this.fontFamily?.default) {
                dom.style.fontFamily = this.fontFamily.default;
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Before the editor check: the first change arrives before the editor exists.
        if (changes['fileDropMode']) {
            this.fileDropHintText = fileDropHint(this.fileDropMode);
        }
        if (!this.editor) {
            return;
        }
        if (changes['value'] && !changes['value'].firstChange) {
            const incoming = changes['value'].currentValue || '';
            if (incoming !== this._lastEmittedValue) {
                this.setValue(incoming);
            }
        }
        if (changes['readonly'] && !changes['readonly'].firstChange) {
            this.editor.setEditable(!this.readonly);
            this.cdr.markForCheck();
        }
        if (changes['ariaLabel'] && !changes['ariaLabel'].firstChange) {
            this.editor.setOptions({ editorProps: { attributes: this._editorAriaAttributes() } });
        }
        if (changes['placeholder'] && !changes['placeholder'].firstChange) {
            const placeholderExt: any = this.editor.extensionManager.extensions.find((e: any) => e.name === 'placeholder');
            if (placeholderExt) {
                placeholderExt.options.placeholder = this.placeholder;
                this.editor.view.dispatch(this.editor.state.tr);
            }
        }
    }

    ngOnDestroy(): void {
        if (this._slashStatusTimer !== null) {
            window.clearTimeout(this._slashStatusTimer);
            this._slashStatusTimer = null;
        }
        if (this._toolbarRo) {
            this._toolbarRo.disconnect();
            this._toolbarRo = null;
        }
        // Pull the slash popup out of <body> before Angular tears down the view,
        // so we don't leak a detached node we previously re-parented.
        const slashEl = this.slashPopup?.nativeElement;
        if (slashEl && slashEl.parentNode === document.body) {
            try { document.body.removeChild(slashEl); } catch { /* noop */ }
        }
        const bubbleEl = this.bubbleMenuEl?.nativeElement;
        if (bubbleEl && bubbleEl.parentNode === document.body) {
            try { document.body.removeChild(bubbleEl); } catch { /* noop */ }
        }
        document.removeEventListener('mousedown', this._onDocumentMouseDown, true);
        if (this.editor) {
            const dom = this.editor.view.dom as HTMLElement;
            dom.removeEventListener('keyup', this._onDomKeyUp);
            dom.removeEventListener('blur', this._onDomBlur);
            dom.removeEventListener('mouseover', this._onDomAnchorHover);
            dom.removeEventListener('mouseleave', this._onDomAnchorLeave);
            dom.removeEventListener('click', this._onDomAnchorClick);
            this.editor.destroy();
            this.editor = null;
        }
        // Release every blob URL produced by the image-resolver during this
        // component's lifetime (image-card NodeViews also revoke their own copy,
        // but the centrally-cached promises live up here).
        this._ownedImageBlobUrls.forEach(u => {
            try { URL.revokeObjectURL(u); } catch { /* ignore */ }
        });
        this._ownedImageBlobUrls = [];
        this._imageUrlCache.clear();
    }

    /**
     * Fetches the bytes of a OneDrive/SharePoint item through the backend proxy
     * (`/api/File/GetOneDriveImage`) and returns a blob: URL usable as <img src>.
     * Results are cached per editor instance, keyed by driveId|fileId, so each
     * image is only fetched once per session.
     */
    private _resolveImageUrl(driveId: string, fileId: string): Promise<string> {
        const key = driveId + '|' + fileId;
        const cached = this._imageUrlCache.get(key);
        if (cached) {
            return cached;
        }
        const base = AppConsts.remoteServiceBaseUrl || '';
        const url = base + '/api/File/GetOneDriveImage'
            + '?driveId=' + encodeURIComponent(driveId)
            + '&itemId=' + encodeURIComponent(fileId);
        const promise = this._http.get(url, { responseType: 'blob' }).toPromise().then(
            (blob: Blob) => {
                const objUrl = URL.createObjectURL(blob);
                this._ownedImageBlobUrls.push(objUrl);
                return objUrl;
            },
            (err) => {
                // Invalidate the cache slot so a future render can retry (e.g. after
                // the user reconnects their OneDrive account).
                this._imageUrlCache.delete(key);
                throw err;
            }
        );
        this._imageUrlCache.set(key, promise);
        return promise;
    }

    // --- Public API (Syncfusion parity) ---

    /** Returns the editor's current HTML content. */
    public getHtml(): string {
        return this.editor ? this.editor.getHTML() : (this.value || '');
    }

    /** Returns the character count (text length, excluding HTML). */
    public getCharCount(): number {
        if (!this.editor) {
            return 0;
        }
        return this.editor.storage.characterCount?.characters() ?? 0;
    }

    /** Sets editor HTML without firing change. */
    public setValue(html: string): void {
        if (!this.editor) {
            this.value = html;
            return;
        }
        this._suppressEmit = true;
        try {
            this.editor.commands.setContent(html || '', false);
            this.charCount = this.editor.storage.characterCount?.characters() ?? 0;
            this._lastEmittedValue = html || '';
            // Whole-document swap (a different task's notes, an AI apply, an Undo-all): nothing applied to the
            // PREVIOUS content is live any more, so stale provisional highlights become sweepable again.
            this._liveAnchorIds.clear();
        } finally {
            this._suppressEmit = false;
        }
        this.cdr.markForCheck();
    }

    /** Inserts raw HTML at the current selection. */
    public insertHtml(html: string): void {
        if (!this.editor) {
            return;
        }
        this.editor.chain().focus().insertContent(html).run();
    }

    /**
     * Insert an inline reference token (file / folder / task / client) at the
     * current selection. Hosts call this from their slash-menu
     * `(slashMenuItemSelect)` handler. The slash menu plugin has already deleted
     * the trigger char + query range, so the token lands at the cursor and the
     * cursor moves after it.
     *
     * Still takes the `MentionCardAttrs` search-row shape hosts already build —
     * it's translated to the unified token here, which is why a searched-for file
     * and an uploaded one render identically.
     */
    public insertMentionCard(attrs: Partial<MentionCardAttrs>): void {
        if (!this.editor) {
            return;
        }
        (this.editor.chain().focus() as any)
            .insertReferenceToken(mentionAttrsToTokenAttrs(attrs))
            .run();
    }

    /**
     * Insert the slash-menu trigger character at the cursor and open the picker.
     *
     * @tiptap/suggestion's default `allowedPrefixes` is `[' ']`, so a bare
     * `insertContent('@')` only opens the picker when the cursor is at the
     * start of a block or directly after whitespace. When the @ button is
     * clicked we want the picker to open regardless of what the user has
     * already typed, so we prepend a space whenever the preceding node is
     * non-whitespace text (or a non-text node like an image card). Slash
     * menu plugin then sees `<space>@` and triggers normally.
     */
    public triggerMention(char?: string): void {
        if (!this.editor) {
            return;
        }
        const triggerChar = char || this.slashTriggerChar || '@';
        const $from = this.editor.state.selection.$from;
        const nodeBefore = $from.nodeBefore;
        let needsSpace = false;
        if (nodeBefore) {
            if (!nodeBefore.isText) {
                needsSpace = true;
            } else {
                const text = nodeBefore.text || '';
                const lastChar = text.slice(-1);
                if (lastChar.length > 0 && !/\s/.test(lastChar)) {
                    needsSpace = true;
                }
            }
        }
        const insert = needsSpace ? (' ' + triggerChar) : triggerChar;
        this.editor.chain().focus().insertContent(insert).run();
    }

    /** Returns true when the editor has a non-empty text selection. */
    public hasSelection(): boolean {
        if (!this.editor) {
            return false;
        }
        const { from, to } = this.editor.state.selection;
        return from !== to;
    }

    /**
     * Viewport rect of the current selection (null when collapsed or unmeasurable) — the same
     * `coordsAtPos` measurement the selection bubble positions itself from, exposed so a host can hang its
     * own UI off the selected passage. Deliberately NOT derived from the comment-anchor highlight: a host
     * that measures the mark it just applied is one broken mark away from a popover pinned at 0,0.
     */
    public getSelectionRect(): { top: number; left: number; bottom: number; right: number } | null {
        if (!this.editor) {
            return null;
        }
        const { from, to, empty } = this.editor.state.selection;
        if (empty) {
            return null;
        }
        try {
            const start = this.editor.view.coordsAtPos(from);
            const end = this.editor.view.coordsAtPos(to);
            return {
                top: Math.min(start.top, end.top),
                left: Math.min(start.left, end.left),
                bottom: Math.max(start.bottom, end.bottom),
                right: Math.max(start.right, end.right),
            };
        } catch {
            return null;
        }
    }

    /** Returns {from, to} positions of the current selection, or null if collapsed. */
    public getSelectionRange(): { from: number; to: number } | null {
        if (!this.editor) {
            return null;
        }
        const { from, to } = this.editor.state.selection;
        if (from === to) {
            return null;
        }
        return { from, to };
    }

    /** Returns serialized HTML for the current selection (empty string if none). */
    public getSelectedHtml(): string {
        if (!this.editor) {
            return '';
        }
        const { from, to } = this.editor.state.selection;
        if (from === to) {
            return '';
        }
        const slice = this.editor.state.selection.content();
        const serializer = DOMSerializer.fromSchema(this.editor.schema);
        const div = document.createElement('div');
        div.appendChild(serializer.serializeFragment(slice.content));
        return div.innerHTML;
    }

    /** Serialized HTML for an explicit document range — the selection-independent `getSelectedHtml`. */
    public getRangeHtml(from: number, to: number): string {
        if (!this.editor) {
            return '';
        }
        const docSize: number = this.editor.state.doc.content.size;
        const safeFrom: number = Math.max(0, Math.min(from, docSize));
        const safeTo: number = Math.max(safeFrom, Math.min(to, docSize));
        if (safeFrom === safeTo) {
            return '';
        }
        const slice = this.editor.state.doc.slice(safeFrom, safeTo);
        const serializer = DOMSerializer.fromSchema(this.editor.schema);
        const div: HTMLDivElement = document.createElement('div');
        div.appendChild(serializer.serializeFragment(slice.content));
        return div.innerHTML;
    }

    /**
     * Widen a range that stops mid-block out to whole text blocks; a range inside ONE block is returned
     * untouched.
     *
     * Proofread needs this. Sending HALF a checklist item to the AI and splicing the corrected sentence
     * back over that half leaves the unselected head behind as a block of its own — which on a checklist
     * reads as an extra checkbox above the corrected one, and in prose as a stray fragment. Whole lines
     * in, whole lines out.
     */
    /**
     * Widen a range so a proofread request receives WHOLE SENTENCES.
     *
     * Handed a fragment, the model completes it: selecting "the cat sat on the " and proofreading returned
     * "The cat sat on the mat." — spliced over the fragment, the original tail survived after it and the
     * text read "...on the mat.mat." Snapping the range out to sentence boundaries first means whatever
     * comes back replaces a whole sentence, so there is nothing left over to duplicate.
     *
     * Cross-block selections defer to expandRangeToBlocks: a block is already a complete unit, and that
     * path also fixes the unselected head being left behind as a list item of its own. This method is a
     * superset of it, so callers only need this one.
     */
    public expandRangeToSentences(from: number, to: number): { from: number; to: number } {
        const TERMINATORS = '.!?';
        let $from: any;
        let $to: any;
        let block: any;
        let text: string;
        let blockStart: number;
        let s: number;
        let e: number;

        if (!this.editor) {
            return { from: from, to: to };
        }
        try {
            $from = this.editor.state.doc.resolve(from);
            $to = this.editor.state.doc.resolve(to);
            if ($from.parent !== $to.parent) {
                return this.expandRangeToBlocks(from, to);
            }
            block = $from.parent;
            if (!block.isTextblock) {
                return { from: from, to: to };
            }
            blockStart = $from.start();
            // One character per inline leaf (mention chips, tokens) keeps these string offsets aligned with
            // document positions, so blockStart + offset is a valid position to hand back.
            text = block.textBetween(0, block.content.size, String.fromCharCode(10), String.fromCharCode(0xFFFC));
            s = Math.max(0, Math.min(from - blockStart, text.length));
            e = Math.max(s, Math.min(to - blockStart, text.length));
            // Back to the start of the sentence the selection begins in, then off any space after the
            // previous terminator so the request does not start with a stray blank.
            while (s > 0 && TERMINATORS.indexOf(text.charAt(s - 1)) < 0) {
                s--;
            }
            while (s < e && text.charAt(s).trim().length === 0) {
                s++;
            }
            // Forward to the end of the sentence the selection ends in — unless it already ends on a
            // terminator, in which case walking on would swallow the NEXT sentence too.
            if (e === 0 || TERMINATORS.indexOf(text.charAt(e - 1)) < 0) {
                while (e < text.length && TERMINATORS.indexOf(text.charAt(e)) < 0) {
                    e++;
                }
                if (e < text.length) {
                    e++;
                }
            }
            if (e <= s) {
                return { from: from, to: to };
            }
            return { from: blockStart + s, to: blockStart + e };
        } catch {
            return { from: from, to: to };
        }
    }

    public expandRangeToBlocks(from: number, to: number): { from: number; to: number } {
        if (!this.editor) {
            return { from: from, to: to };
        }
        try {
            const $from = this.editor.state.doc.resolve(from);
            const $to = this.editor.state.doc.resolve(to);
            if ($from.parent === $to.parent) {
                return { from: from, to: to };
            }
            const start: number = $from.parent.isTextblock ? $from.start() : from;
            const end: number = $to.parent.isTextblock ? $to.end() : to;
            return { from: Math.min(start, from), to: Math.max(end, to) };
        } catch {
            return { from: from, to: to };
        }
    }

    /**
     * Replaces the [from, to] range with the provided HTML and focuses the editor.
     * Outer block wrappers (single <p>, <ul><li>, <ol><li>, <li>) are stripped so
     * inline selections stay inline (no extra paragraph breaks introduced) and
     * list-item selections replace the inline content within the original <li>.
     *
     * If the replacement target sits inside a table cell, the input is
     * flattened to inline-only content (block-level wrappers, including
     * tables, are dropped) so ProseMirror doesn't materialize a nested
     * table inside the cell when the AI returns block-wrapped HTML.
     */
    public replaceRange(from: number, to: number, html: string): void {
        if (!this.editor) {
            return;
        }
        const docSize = this.editor.state.doc.content.size;
        const safeFrom = Math.max(0, Math.min(from, docSize));
        const safeTo = Math.max(safeFrom, Math.min(to, docSize));
        if (this._isPositionInTableCell(safeFrom)) {
            this.editor.chain().focus()
                .insertContentAt({ from: safeFrom, to: safeTo }, this._flattenToInline(html || ''))
                .run();
            return;
        }
        // Structure-preserving path, tried first for a multi-block range: swap each covered block's
        // INLINE content and leave every node in place, so a checklist keeps exactly its items. Splicing
        // block-level HTML in instead is what grew the list by one item per proofread.
        if (this._replaceBlocksInRange(safeFrom, safeTo, html || '')) {
            return;
        }
        this.editor.chain().focus()
            .insertContentAt({ from: safeFrom, to: safeTo }, this._prepareRangeHtml(safeFrom, safeTo, html || ''))
            .run();
    }

    /**
     * Replace a whole-block range block-by-block: the replacement's leaf blocks are matched 1:1 with the
     * text blocks the range covers and each one's inline content is swapped in place. The surrounding
     * structure is untouched BY CONSTRUCTION — no node is inserted or removed — so proofreading three
     * checklist items can only ever return three checklist items.
     *
     * Declines (returns false, caller falls back to the splice path) unless it can be exact: the range
     * must cover every block it touches in full, and the block counts must line up. That keeps callers
     * that pass a partial range — or an AI reply that merged the lines — on the previous behaviour.
     */
    private _replaceBlocksInRange(from: number, to: number, html: string): boolean {
        if (!this.editor) {
            return false;
        }
        // BLANK lines are matched on neither side and never touched. They carry no text to proofread, and
        // whether one comes back in the reply is up to the model — pairing them up is what made the counts
        // disagree, dropping the whole thing onto the splice path, which then ate the blank rows.
        const blocks: string[] = this._extractInlineBlocks(html).filter((block) => this._hasText(block));
        if (blocks.length < 2) {
            return false;
        }
        const targets: { from: number; to: number }[] = [];
        let coversWholeBlocks: boolean = true;
        this.editor.state.doc.nodesBetween(from, to, (node, pos) => {
            if (!node.isTextblock) {
                return true;
            }
            if (node.content.size === 0) {
                return false;
            }
            const contentFrom: number = pos + 1;
            const contentTo: number = contentFrom + node.content.size;
            if (contentFrom < from || contentTo > to) {
                coversWholeBlocks = false;
            }
            targets.push({ from: contentFrom, to: contentTo });
            return false;
        });
        if (!coversWholeBlocks || targets.length !== blocks.length) {
            return false;
        }
        // Back to front: replacing a later block never shifts an earlier block's positions, so every
        // range stays valid across the single transaction.
        const chain = this.editor.chain().focus();
        for (let i = targets.length - 1; i >= 0; i--) {
            chain.insertContentAt({ from: targets[i].from, to: targets[i].to }, blocks[i]);
        }
        chain.run();
        return true;
    }

    /**
     * The inline HTML of each LEAF block in the input, in document order.
     *
     * A checklist / list item serializes as `<li …><label>…</label><div><p>text</p></div></li>`, so the
     * paragraph-and-heading set is the right granularity for both a list and a run of paragraphs. A bare
     * `<li>text</li>` (which the AI sometimes returns) counts too, but a `<li>` that still holds a
     * paragraph does not — otherwise one item would be counted twice.
     */
    /**
     * The visible text of an HTML fragment with whitespace collapsed — case and punctuation INTACT, unlike
     * `_extractNormalizedTokens`. Used to answer "did anything actually change?" when the normalized word
     * diff says zero.
     */
    private _visibleTextOf(html: string): string {
        const parser: HTMLDivElement = document.createElement('div');
        parser.innerHTML = html || '';
        return (parser.textContent || '').replace(/\s+/g, ' ').trim();
    }

    /** True when the HTML fragment carries visible text (tags and nbsp alone don't count). */
    private _hasText(html: string): boolean {
        return (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;
    }

    private _extractInlineBlocks(html: string): string[] {
        if (!html) {
            return [];
        }
        const wrapper: HTMLDivElement = document.createElement('div');
        wrapper.innerHTML = html.trim();
        // The taskItem checkbox carries no text of its own and must not read as content.
        wrapper.querySelectorAll('label').forEach((el: Element) => el.remove());
        const out: string[] = [];
        wrapper.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li').forEach((el: Element) => {
            if (el.querySelector('p, h1, h2, h3, h4, h5, h6, li')) {
                return;
            }
            out.push(el.innerHTML);
        });
        return out;
    }

    /** True when `pos` resolves to a position inside a tableCell or tableHeader. */
    private _isPositionInTableCell(pos: number): boolean {
        if (!this.editor) {
            return false;
        }
        try {
            const $pos = this.editor.state.doc.resolve(pos);
            for (let d = $pos.depth; d > 0; d--) {
                const nodeType: string = $pos.node(d).type.name;
                if (nodeType === 'tableCell' || nodeType === 'tableHeader') {
                    return true;
                }
            }
        } catch {
            return false;
        }
        return false;
    }

    /**
     * Decide how replacement HTML should be shaped for the range it is going to occupy.
     *
     * A selection INSIDE one text block wants inline content — that is `_stripOuterBlocks`, and it is
     * what the original implementation always did. But a selection that spans block boundaries (two
     * list items, two paragraphs) gets block-level HTML back from the AI, and splicing that in as if it
     * were inline is what produced the corrupted notes: proofreading across two `<li>`s inserted a whole
     * nested `<ol>` inside the first item, stranding a stray character before it and the tail of the
     * second item after it.
     *
     * When the range spans blocks and we are already inside a list, one outer list wrapper is dropped so
     * the returned `<li>`s land as SIBLINGS of the items being replaced rather than as a nested list.
     * Otherwise the block HTML goes in untouched.
     */
    private _prepareRangeHtml(from: number, to: number, html: string): string {
        if (this._isInsideSingleTextBlock(from, to)) {
            return this._stripOuterBlocks(html);
        }
        return this._isPositionInList(from) ? this._unwrapOuterList(html) : html;
    }

    /**
     * True only when the whole range lives inside ONE text block — the case where the replacement has to
     * be inline. A select-all resolves BOTH ends to the doc node itself, which is not a text block: it
     * used to satisfy the old "same parent" test and get stripped to inline content, so proofreading a
     * whole one-item checklist flattened the item into a plain paragraph.
     */
    private _isInsideSingleTextBlock(from: number, to: number): boolean {
        if (!this.editor) {
            return false;
        }
        try {
            const $from = this.editor.state.doc.resolve(from);
            const $to = this.editor.state.doc.resolve(to);
            return $from.parent === $to.parent && $from.parent.isTextblock;
        } catch {
            return false;
        }
    }

    /** True when `pos` resolves inside a listItem / taskItem. */
    private _isPositionInList(pos: number): boolean {
        if (!this.editor) {
            return false;
        }
        try {
            const $pos = this.editor.state.doc.resolve(pos);
            for (let d = $pos.depth; d > 0; d--) {
                const name: string = $pos.node(d).type.name;
                if (name === 'listItem' || name === 'taskItem') {
                    return true;
                }
            }
        } catch {
            return false;
        }
        return false;
    }

    /** Strip ONE outer `<ul>`/`<ol>`, returning its `<li>` children as a fragment. */
    private _unwrapOuterList(html: string): string {
        if (!html) {
            return '';
        }
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html.trim();
        const only = wrapper.children.length === 1 ? wrapper.firstElementChild : null;
        if (only && (only.tagName === 'UL' || only.tagName === 'OL')) {
            return only.innerHTML;
        }
        return html;
    }

    /**
     * Walks the input HTML extracting inline-level content only. Block
     * elements (P, DIV, TABLE, TR, TD, UL, OL, LI, etc.) are recursed into
     * but their tags are dropped. Inline tags (B, I, U, A, SPAN, BR, etc.)
     * are preserved with their attributes. Used when replacing content
     * inside a table cell, where inserting block-level structure (especially
     * a TABLE) would cause ProseMirror to create a nested table inside
     * the cell.
     */
    private _flattenToInline(html: string): string {
        if (!html) {
            return '';
        }
        const wrapper: HTMLDivElement = document.createElement('div');
        wrapper.innerHTML = html.trim();
        const inlineTags: Set<string> = new Set<string>([
            'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE', 'DEL', 'INS',
            'SPAN', 'A', 'CODE', 'MARK', 'SUB', 'SUP', 'BR', 'IMG',
            'KBD', 'SAMP', 'CITE', 'Q', 'TIME', 'ABBR', 'SMALL'
        ]);
        const walk = (node: Node): string => {
            let out: string = '';
            node.childNodes.forEach(child => {
                if (child.nodeType === Node.TEXT_NODE) {
                    out += child.textContent || '';
                    return;
                }
                if (child.nodeType !== Node.ELEMENT_NODE) {
                    return;
                }
                const el: HTMLElement = child as HTMLElement;
                const tag: string = el.tagName.toUpperCase();
                if (inlineTags.has(tag)) {
                    const tagLower: string = tag.toLowerCase();
                    let attrs: string = '';
                    for (let i = 0; i < el.attributes.length; i++) {
                        const attr: Attr = el.attributes[i];
                        attrs += ' ' + attr.name + '="' + attr.value.replace(/"/g, '&quot;') + '"';
                    }
                    if (tag === 'BR' || tag === 'IMG') {
                        out += '<' + tagLower + attrs + '>';
                    } else {
                        out += '<' + tagLower + attrs + '>' + walk(el) + '</' + tagLower + '>';
                    }
                } else {
                    // Block element — drop its tag, keep its inline content
                    out += walk(el);
                    // Add a separator between sibling block elements so words
                    // don't run together when paragraphs/cells are flattened.
                    if (out && !out.endsWith(' ') && !out.endsWith('\n') && !out.endsWith('>')) {
                        out += ' ';
                    }
                }
            });
            return out;
        };
        return walk(wrapper).trim();
    }

    private _stripOuterBlocks(html: string): string {
        if (!html) {
            return '';
        }
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html.trim();

        // Iteratively unwrap a single outer block element so the resulting HTML
        // can be inserted as inline content into an existing block.
        let safety = 6;
        while (safety-- > 0) {
            const onlyChild = wrapper.children.length === 1 ? wrapper.firstElementChild : null;
            if (!onlyChild) {
                break;
            }
            const tag = onlyChild.tagName;
            if (tag === 'P' || tag === 'DIV' || tag === 'LI') {
                wrapper.innerHTML = onlyChild.innerHTML;
                continue;
            }
            if (tag === 'UL' || tag === 'OL') {
                const li = onlyChild.children.length === 1 && onlyChild.firstElementChild?.tagName === 'LI'
                    ? onlyChild.firstElementChild
                    : null;
                if (li) {
                    wrapper.innerHTML = li.innerHTML;
                    continue;
                }
            }
            break;
        }

        return wrapper.innerHTML;
    }

    /** Focuses the editor. */
    public focusIn(): void {
        if (this.editor) {
            this.editor.commands.focus();
        }
    }

    /** Sets editable mode. */
    public setReadonly(readonly: boolean): void {
        this.readonly = readonly;
        if (this.editor) {
            this.editor.setEditable(!readonly);
        }
        this.cdr.markForCheck();
    }

    /** Forces a re-render of the wrapper. */
    public refreshUI(): void {
        if (this.editor) {
            this.editor.view.updateState(this.editor.state);
        }
        this.cdr.markForCheck();
    }

    /** Returns the contenteditable host element. */
    public getEditPanel(): HTMLElement | null {
        return this.editor ? (this.editor.view.dom as HTMLElement) : null;
    }

    /** Print the editor content. */
    public print(): void {
        const win = window.open('', '_blank');
        if (!win) {
            return;
        }
        win.document.write('<html><head><title>Print</title></head><body>' + this.getHtml() + '</body></html>');
        win.document.close();
        win.focus();
        win.print();
    }

    /** Toggle fullscreen state. */
    public showFullScreen(): void {
        this._isFullScreen = true;
        this.host.nativeElement.classList.add('vf-tiptap-fullscreen');
    }

    public hideFullScreen(): void {
        this._isFullScreen = false;
        this.host.nativeElement.classList.remove('vf-tiptap-fullscreen');
    }

    public get isFullScreen(): boolean {
        return this._isFullScreen;
    }

    /**
     * Drives an editor command. Argument `value` is used by `insertHTML`,
     * `fontColor`, `backgroundColor`, `fontSize`, and `createLink`.
     */
    public executeCommand(command: RteCommand | string, value?: any): void {
        if (!this.editor) {
            return;
        }
        const chain = this.editor.chain().focus();
        switch (command) {
            case 'bold':
                chain.toggleBold().run();
                break;
            case 'italic':
                chain.toggleItalic().run();
                break;
            case 'underline':
                chain.toggleUnderline().run();
                break;
            case 'strikeThrough':
                chain.toggleStrike().run();
                break;
            case 'insertUnorderedList':
                chain.toggleBulletList().run();
                break;
            case 'insertOrderedList':
                chain.toggleOrderedList().run();
                break;
            case 'insertTaskList':
                chain.toggleTaskList().run();
                break;
            case 'indent':
                this._runIndent();
                break;
            case 'outdent':
                this._runOutdent();
                break;
            case 'undo':
                chain.undo().run();
                break;
            case 'redo':
                chain.redo().run();
                break;
            case 'insertHTML':
                if (value) {
                    chain.insertContent(value).run();
                }
                break;
            case 'fontColor':
                if (value) {
                    chain.setColor(value).run();
                } else {
                    chain.unsetColor().run();
                }
                break;
            case 'backgroundColor':
                if (value && value !== 'transparent') {
                    chain.toggleHighlight({ color: value }).run();
                } else {
                    chain.unsetHighlight().run();
                }
                break;
            case 'fontSize':
                if (value) {
                    (chain as any).setFontSize(value).run();
                } else {
                    (chain as any).unsetFontSize().run();
                }
                break;
            case 'createLink': {
                if (typeof value === 'string') {
                    if (value) {
                        chain.extendMarkRange('link').setLink({ href: value, target: '_blank' }).run();
                    }
                } else {
                    const currentHref = this.editor.getAttributes('link').href || '';
                    this.openLinkInputPopup('add', currentHref);
                }
                break;
            }
            case 'unlink':
                chain.unsetLink().run();
                break;
            case 'insertTable':
                chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
                break;
            case 'addRowBefore':
                chain.addRowBefore().run();
                break;
            case 'addRowAfter':
                chain.addRowAfter().run();
                break;
            case 'addColumnBefore':
                chain.addColumnBefore().run();
                break;
            case 'addColumnAfter':
                chain.addColumnAfter().run();
                break;
            case 'deleteRow':
                chain.deleteRow().run();
                break;
            case 'deleteColumn':
                chain.deleteColumn().run();
                break;
            case 'deleteTable':
                chain.deleteTable().run();
                break;
            case 'toggleHeaderRow':
                chain.toggleHeaderRow().run();
                break;
            case 'mergeOrSplit':
                chain.mergeOrSplit().run();
                break;
            default:
                break;
        }
    }

    /** True when the current selection sits inside a table — drives table-edit
     *  buttons in the bubble menu (and the contextual section of the toolbar's table dropdown). */
    public isTableActive(): boolean {
        if (!this.editor) {
            return false;
        }
        try {
            return this.editor.isActive('table');
        } catch {
            return false;
        }
    }

    /**
     * Replace the current slash menu items. Used by callers that populate items
     * asynchronously (file search, mention filter, etc.).
     */
    public set slashMenuItems(items: SlashMenuItem[]) {
        this._slashItemsBuffer = this._trustRowIcons(items || []);
        if (this.slashVisible) {
            this.slashItems = this._slashItemsBuffer;
            if (this.slashSelectedIndex >= this.slashItems.length) {
                this.slashSelectedIndex = 0;
            }
            this._slashAnswered = true;
            this._syncSlashAccessibility();
            this.cdr.markForCheck();
        }
    }
    public get slashMenuItems(): SlashMenuItem[] {
        return this._slashItemsBuffer;
    }

    public get element(): HTMLElement {
        return this.host.nativeElement;
    }

    // --- Bubble menu actions ---

    public bubbleAction(name: string, value?: any): void {
        this.executeCommand(name as any, value);
        this.showFontColorPicker = false;
        this.showHighlightPicker = false;
        this.showFontSizeMenu = false;
        this.showBubbleTableDeleteMenu = false;
    }

    public isMarkActive(name: string): boolean {
        if (!this.editor) {
            return false;
        }
        try {
            return this.editor.isActive(name);
        } catch {
            return false;
        }
    }

    public toggleColorPicker(ev: MouseEvent): void {
        ev.preventDefault();
        ev.stopPropagation();
        this.showFontColorPicker = !this.showFontColorPicker;
        this.showHighlightPicker = false;
        this.showFontSizeMenu = false;
        this.showBubbleTableDeleteMenu = false;
    }

    public toggleHighlightPicker(ev: MouseEvent): void {
        ev.preventDefault();
        ev.stopPropagation();
        this.showHighlightPicker = !this.showHighlightPicker;
        this.showFontColorPicker = false;
        this.showFontSizeMenu = false;
        this.showBubbleTableDeleteMenu = false;
    }

    public toggleFontSizeMenu(ev: MouseEvent): void {
        ev.preventDefault();
        ev.stopPropagation();
        this.showFontSizeMenu = !this.showFontSizeMenu;
        this.showFontColorPicker = false;
        this.showHighlightPicker = false;
        this.showBubbleTableDeleteMenu = false;
    }

    public toggleBubbleTableDeleteMenu(ev: MouseEvent): void {
        ev.preventDefault();
        ev.stopPropagation();
        this.showBubbleTableDeleteMenu = !this.showBubbleTableDeleteMenu;
        this.showFontColorPicker = false;
        this.showHighlightPicker = false;
        this.showFontSizeMenu = false;
    }

    // --- Toolbar actions ---

    public toolbarAction(name: string, value?: any): void {
        this.executeCommand(name as any, value);
        this.showTbColorPicker = false;
        this.showTbHighlightPicker = false;
        this.showTbFontSizeMenu = false;
        this.showTbTableMenu = false;
        this.showTbWriteAiMenu = false;
    }

    public toggleTbWriteAiMenu(ev: MouseEvent): void {
        ev.preventDefault();
        ev.stopPropagation();
        this.showTbWriteAiMenu = !this.showTbWriteAiMenu;
        if (this.showTbWriteAiMenu) {
            this.positionTbWriteAiMenu(ev.currentTarget as HTMLElement);
        }
        this.showTbColorPicker = false;
        this.showTbHighlightPicker = false;
        this.showTbFontSizeMenu = false;
        this.showTbTableMenu = false;
    }

    /** Pin the menu under its button, flipping above when the space below is too short to hold it and
     *  nudging left when it would run off the right edge. */
    private positionTbWriteAiMenu(button: HTMLElement): void {
        const MENU_WIDTH = 320;
        const MENU_HEIGHT = 124;
        const GAP = 4;
        const MARGIN = 8;
        let rect: DOMRect = null;
        let origin: { x: number; y: number } = null;
        let left = 0;
        let top = 0;
        if (!button || !button.getBoundingClientRect) {
            this.tbWriteAiMenuStyle = {};
            return;
        }
        rect = button.getBoundingClientRect();
        left = Math.max(MARGIN, Math.min(rect.left, window.innerWidth - MENU_WIDTH - MARGIN));
        top = rect.bottom + GAP + MENU_HEIGHT > window.innerHeight - MARGIN && rect.top - GAP - MENU_HEIGHT > MARGIN
            ? rect.top - GAP - MENU_HEIGHT
            : rect.bottom + GAP;
        // `position: fixed` only resolves against the viewport when NO ancestor is a containing block for
        // it. The task panel's modal-dialog carries an (identity) transform, which is enough to make it
        // one, and the menu was landing offset by the dialog's own origin. Re-base onto whatever the
        // containing block actually is, so the same numbers work wherever this editor is mounted.
        origin = this.fixedContainingBlockOrigin(button);
        this.tbWriteAiMenuStyle = { left: (left - origin.x) + 'px', top: (top - origin.y) + 'px' };
    }

    /**
     * Viewport offset of the containing block a `position: fixed` child would resolve against — {0,0}
     * when that is the viewport itself. An ancestor takes the job as soon as it has a transform,
     * perspective, filter, backdrop-filter, a will-change naming one of those, or a `contain` that
     * includes layout or paint; the containing block is then that ancestor's PADDING box.
     */
    private fixedContainingBlockOrigin(from: HTMLElement): { x: number; y: number } {
        let node: HTMLElement = from ? from.parentElement : null;
        let style: CSSStyleDeclaration = null;
        let box: DOMRect = null;
        while (node && node !== document.documentElement) {
            style = window.getComputedStyle(node);
            if ((style.transform && style.transform !== 'none')
                || (style.perspective && style.perspective !== 'none')
                || (style.filter && style.filter !== 'none')
                || ((style as any).backdropFilter && (style as any).backdropFilter !== 'none')
                || /transform|perspective|filter/.test(style.willChange || '')
                || /layout|paint|strict|content/.test((style as any).contain || '')) {
                box = node.getBoundingClientRect();
                return {
                    x: box.left + (parseFloat(style.borderLeftWidth) || 0),
                    y: box.top + (parseFloat(style.borderTopWidth) || 0)
                };
            }
            node = node.parentElement;
        }
        return { x: 0, y: 0 };
    }

    public pickWriteAiOption(option: 'proofread' | 'ask'): void {
        this.showTbWriteAiMenu = false;
        if (option === 'proofread') {
            this.emitProofread();
        } else {
            this.askAiAction.emit();
        }
    }

    public toggleTbColorPicker(ev: MouseEvent): void {
        ev.preventDefault();
        ev.stopPropagation();
        this.showTbColorPicker = !this.showTbColorPicker;
        this.showTbHighlightPicker = false;
        this.showTbFontSizeMenu = false;
        this.showTbTableMenu = false;
        this.showTbWriteAiMenu = false;
    }

    public toggleTbHighlightPicker(ev: MouseEvent): void {
        ev.preventDefault();
        ev.stopPropagation();
        this.showTbHighlightPicker = !this.showTbHighlightPicker;
        this.showTbColorPicker = false;
        this.showTbFontSizeMenu = false;
        this.showTbTableMenu = false;
        this.showTbWriteAiMenu = false;
    }

    public toggleTbFontSizeMenu(ev: MouseEvent): void {
        ev.preventDefault();
        ev.stopPropagation();
        this.showTbFontSizeMenu = !this.showTbFontSizeMenu;
        this.showTbColorPicker = false;
        this.showTbHighlightPicker = false;
        this.showTbTableMenu = false;
        this.showTbWriteAiMenu = false;
    }

    public toggleTbTableMenu(ev: MouseEvent): void {
        ev.preventDefault();
        ev.stopPropagation();
        this.showTbTableMenu = !this.showTbTableMenu;
        this.showTbColorPicker = false;
        this.showTbHighlightPicker = false;
        this.showTbFontSizeMenu = false;
        this.showTbWriteAiMenu = false;
    }

    public emitProofread(): void {
        this.proofreadAction.emit();
    }

    /**
     * Bubble "New comment" (opt-in via showNewComment): emit the current selection's text + range so a
     * host can open a comment composer pre-filled with the quote (anchoring lands in a later phase).
     * No-op when there is no selection.
     */
    public emitNewComment(): void {
        if (!this.editor) {
            return;
        }
        const { from, to } = this.editor.state.selection;
        if (from === to) {
            return;
        }
        const text = this.editor.state.doc.textBetween(from, to, ' ').trim();
        if (!text) {
            return;
        }
        this.newCommentAction.emit({ text, from, to, overlapAnchorId: this.anchorIdInRange(from, to) });
        // Dismiss the selection bubble: it hides on an empty selection (see _updateBubbleMenuPosition), so
        // collapse to the end of the range. The from/to are already captured above for the anchor, and the
        // host applies the highlight on post — this only clears the transient blue selection + the bubble.
        this.editor.commands.setTextSelection(to);
    }

    /** The anchor id of an existing comment-anchor mark overlapping [from,to] (drives overlap→reply), else
     *  null. Scans the selection range for the first text node carrying a commentAnchor mark. */
    private anchorIdInRange(from: number, to: number): string | null {
        if (!this.editor) {
            return null;
        }
        const markType = this.editor.state.schema.marks['commentAnchor'];
        if (!markType) {
            return null;
        }
        let found: string | null = null;
        this.editor.state.doc.nodesBetween(from, to, (node: any) => {
            if (found || !node.isText) {
                return;
            }
            const m = node.marks.find((mk: any) => mk.type === markType && mk.attrs.anchorId);
            if (m) {
                found = m.attrs.anchorId;
            }
        });
        return found;
    }

    /** Anchored comments (Unified Task Detail): wrap the current selection in a highlight tagged `anchorId`. */
    public applyCommentAnchor(anchorId: string): void {
        if (!this.editor) {
            return;
        }
        const { from, to } = this.editor.state.selection;
        this.applyCommentAnchorRange(anchorId, from, to);
    }

    /**
     * Apply the anchor highlight to a specific range — on comment post, and on composer OPEN so the passage
     * is marked while you write about it. No-op if the range is out of bounds.
     *
     * Marks the range DIRECTLY, the same way `syncCommentAnchors` does, rather than moving the selection
     * onto it and running the mark command: that combination applied nothing (see the note on
     * `setCommentAnchor`) and, even once fixed, moving the selection is a side effect this has no business
     * having — the editor collapses the selection right after to dismiss the bubble menu, and the caller
     * measured the passage from it.
     */
    public applyCommentAnchorRange(anchorId: string, from: number, to: number): void {
        if (!this.editor || from == null || to == null || from === to) {
            return;
        }
        const { state } = this.editor;
        const markType = state.schema.marks['commentAnchor'];
        if (!markType) {
            return;
        }
        if (from < 0 || to > state.doc.content.size) {
            return;
        }
        const tr = state.tr;
        tr.addMark(from, to, markType.create({ anchorId }));
        // Bookkeeping, not something the user typed — Ctrl+Z should reach their last real edit.
        tr.setMeta('addToHistory', false);
        this._liveAnchorIds.add(anchorId);
        this.editor.view.dispatch(tr);
    }

    /**
     * Anchor ids applied in THIS session and not yet renamed or removed. They belong to an open composer (or
     * a comment created moments ago) rather than to the saved notes, so `syncCommentAnchors` must not sweep
     * them away as stale — the mark it applies fires a change, which re-enters sync, which used to delete the
     * highlight in the same tick it appeared. Cleared by `setValue`, i.e. whenever a different note loads, so
     * a `pending-…` span left in saved HTML by an abandoned draft IS still cleaned up.
     */
    private _liveAnchorIds = new Set<string>();

    /**
     * Reconcile a provisional anchor id to the real one after the comment is created — renamed IN PLACE so
     * the highlight keeps its exact range and its id matches the stream entry's `c{id}` (for highlight↔entry
     * linking). Dispatched directly, kept out of the undo history.
     */
    public renameCommentAnchor(oldId: string, newId: string): void {
        if (!this.editor || !oldId || !newId || oldId === newId) {
            return;
        }
        const { state } = this.editor;
        const markType = state.schema.marks['commentAnchor'];
        if (!markType) {
            return;
        }
        const tr = state.tr;
        let changed = false;
        state.doc.descendants((node: any, pos: number) => {
            if (!node.isText) {
                return;
            }
            const has = node.marks.some((m: any) => m.type === markType && m.attrs.anchorId === oldId);
            if (has) {
                tr.removeMark(pos, pos + node.nodeSize, markType);
                tr.addMark(pos, pos + node.nodeSize, markType.create({ anchorId: newId }));
                changed = true;
            }
        });
        if (changed) {
            // The highlight is the same one, under its real id now — carry its "applied this session" status
            // across so the re-projection keeps it until the comment shows up in the stream data.
            this._liveAnchorIds.delete(oldId);
            this._liveAnchorIds.add(newId);
            tr.setMeta('addToHistory', false);
            this.editor.view.dispatch(tr);
        }
    }

    /**
     * Re-project the comment anchors onto the notes: make the highlighted spans exactly match the given
     * (id, range) list. Used on load to upgrade legacy/provisional ids to their real `c{id}` so OLD anchored
     * comments link too, and to self-heal drift. Skips entirely (no dispatch → no save) when the doc's anchor
     * ids already match the target set and none are provisional — so it doesn't churn on every rebuild.
     */
    public syncCommentAnchors(anchors: { id: string; quote: string }[]): void {
        if (!this.editor) {
            return;
        }
        const { state } = this.editor;
        const markType = state.schema.marks['commentAnchor'];
        if (!markType) {
            return;
        }
        const quoteById = new Map(anchors.map((a) => [a.id, (a.quote || '').trim()]));
        // Anything in the target list has LANDED — the stream data now vouches for it, so it no longer needs
        // the "applied this session" protection below. Without this prune an id kept that protection for the
        // whole session, and the sweep could never reclaim it: a highlight outlived its comment being
        // deleted or resolved, leaving a passage marked with nothing behind it until the task was reopened.
        for (const a of anchors) {
            this._liveAnchorIds.delete(a.id);
        }
        const runs = this.collectAnchorRuns(markType);

        // A run is already CORRECT when its id matches a comment and its text is exactly that comment's quote.
        // Correct runs are KEPT as-is (this preserves edit-tracked positions, incl. a quote that legitimately
        // appears more than once). Every other run (wrong text / stale range / unmatched provisional id) is
        // removed, and any comment left without a correct highlight is re-placed by TEXT search below.
        const keep = new Set<{ id: string; from: number; to: number; text: string }>();
        const satisfied = new Set<string>();
        const used: { from: number; to: number }[] = [];
        for (const run of runs) {
            const q = quoteById.get(run.id);
            if (q != null && q.length > 0 && run.text.trim() === q) {
                keep.add(run);
                satisfied.add(run.id);
                used.push({ from: run.from, to: run.to });
                continue;
            }
            // Applied in this session and not in `anchors` yet — an open composer's provisional highlight, or
            // one whose comment hasn't landed in the stream data. Keep it, and reserve its range so a
            // text-search placement doesn't land on top of it.
            if (this._liveAnchorIds.has(run.id)) {
                keep.add(run);
                used.push({ from: run.from, to: run.to });
            }
        }
        // Place a highlight for each anchored comment not already satisfied — first occurrence of its quote in
        // the CURRENT doc (robust to edits) that isn't already taken by another anchor.
        const placements: { id: string; from: number; to: number }[] = [];
        for (const a of anchors) {
            const q = (a.quote || '').trim();
            if (!q || satisfied.has(a.id)) {
                continue;
            }
            const r = this.findQuoteRange(q, used);
            if (r) {
                placements.push({ id: a.id, from: r.from, to: r.to });
                used.push(r);
            }
        }
        const removeRuns = runs.filter((run) => !keep.has(run));
        if (!removeRuns.length && !placements.length) {
            return; // already in sync — no dispatch, no save churn
        }
        const tr = state.tr;
        for (const run of removeRuns) {
            tr.removeMark(run.from, run.to, markType);
        }
        for (const p of placements) {
            tr.addMark(p.from, p.to, markType.create({ anchorId: p.id }));
        }
        tr.setMeta('addToHistory', false);
        this.editor.view.dispatch(tr);
    }

    /** Contiguous same-id comment-anchor runs in the doc, with their text (drives the sync/narrowing logic). */
    private collectAnchorRuns(
        markType: any,
    ): { id: string; from: number; to: number; text: string }[] {
        const runs: { id: string; from: number; to: number; text: string }[] = [];
        let cur: { id: string; from: number; to: number; text: string } | null = null;
        const flush = () => {
            if (cur) {
                runs.push(cur);
                cur = null;
            }
        };
        this.editor.state.doc.descendants((node: any, pos: number) => {
            if (!node.isText) {
                flush();
                return;
            }
            const m = node.marks.find((mk: any) => mk.type === markType && mk.attrs.anchorId);
            const id = m ? m.attrs.anchorId : null;
            if (id == null) {
                flush();
                return;
            }
            const from = pos;
            const to = pos + node.nodeSize;
            if (cur && cur.id === id && cur.to === from) {
                cur.to = to;
                cur.text += node.text || '';
            } else {
                flush();
                cur = { id, from, to, text: node.text || '' };
            }
        });
        flush();
        return runs;
    }

    /** First occurrence of `quote` in the doc (searched per text node) not overlapping a `used` range; else null. */
    private findQuoteRange(
        quote: string,
        used: { from: number; to: number }[],
    ): { from: number; to: number } | null {
        let result: { from: number; to: number } | null = null;
        this.editor.state.doc.descendants((node: any, pos: number) => {
            if (result || !node.isText) {
                return;
            }
            const t: string = node.text || '';
            let searchFrom = 0;
            for (;;) {
                const idx = t.indexOf(quote, searchFrom);
                if (idx < 0) {
                    break;
                }
                const from = pos + idx;
                const to = from + quote.length;
                if (!used.some((u) => from < u.to && to > u.from)) {
                    result = { from, to };
                    return;
                }
                searchFrom = idx + 1;
            }
        });
        return result;
    }

    /** Remove a comment-anchor highlight by id (on resolve). */
    public removeCommentAnchor(anchorId: string): void {
        if (!this.editor) {
            return;
        }
        this._liveAnchorIds.delete(anchorId);
        (this.editor.chain() as any).unsetCommentAnchorById(anchorId).run();
    }

    /**
     * Emphasise the anchor highlight with this id (hover linking from the stream) — toggles
     * `vf-anchor--focus` on the matching rendered span(s) and clears it from the rest. Operates on the
     * editor's OWN rendered DOM (view.dom), not a global query.
     */
    public focusCommentAnchor(anchorId: string | null): void {
        if (!this.editor) {
            return;
        }
        const spans = this.editor.view.dom.querySelectorAll('span[data-comment-id]');
        spans.forEach((el: Element) => {
            const match = anchorId != null && el.getAttribute('data-comment-id') === anchorId;
            el.classList.toggle('vf-anchor--focus', match);
        });
    }

    /** Scroll the anchor highlight into view and flash it (click-to-jump from a stream entry's quote).
     *  Returns false when the span isn't present yet (editor not mounted / not reconciled) so callers can
     *  retry after expanding a collapsed notes editor. */
    public scrollToCommentAnchor(anchorId: string): boolean {
        if (!this.editor || !anchorId) {
            return false;
        }
        const el = this.editor.view.dom.querySelector(
            'span[data-comment-id="' + anchorId + '"]',
        ) as HTMLElement | null;
        if (!el) {
            return false;
        }
        el.scrollIntoView({ block: 'center', behavior: ReducedMotionHelper.scrollBehavior() });
        el.classList.remove('vf-anchor--flash');
        // Force reflow so the flash animation restarts even when the same anchor is clicked twice.
        void el.offsetWidth;
        el.classList.add('vf-anchor--flash');
        setTimeout(() => el.classList.remove('vf-anchor--flash'), 1000);
        return true;
    }

    /**
     * Show the post-Proofread review pill. The host passes the pre-Proofread
     * HTML so "Undo all" can restore it exactly.
     *
     * When `label` is omitted, the editor computes the change count by diffing
     * normalized word tokens between the pre- and post-Proofread HTML — so the
     * pill shows the actual number of edits ("3 changes to review"). If the
     * diff finds zero meaningful changes, the pill is suppressed entirely
     * (returning 0) — proofread should never report "1 change" against
     * identical content, and the host can show its own "no changes" feedback
     * if it wants.
     *
     * Returns the change count the pill displayed (0 = pill not shown).
     */
    public showProofreadReview(beforeHtml: string, label?: string): number {
        let count: number = -1;
        if (label !== undefined && label !== null) {
            this.proofreadReviewLabel = label;
        } else {
            count = this._countWordChanges(beforeHtml || '', this.getHtml() || '');
            // The word diff normalizes case and edge punctuation away (deliberately — it stops an AI's
            // typographic rewrite reporting "130 changes"). But when that lands on ZERO and the text DID
            // change, proofread was applying a real fix — a capitalised sentence, a added full stop — while
            // telling the user "your text looks good". Report it as one change so the edit is reviewable.
            if (count === 0 && this._visibleTextOf(beforeHtml || '') !== this._visibleTextOf(this.getHtml() || '')) {
                count = 1;
            }
            if (count === 0) {
                // No real changes — don't show the pill at all. Just clear
                // the in-progress flag and bail. The snapshot is also cleared
                // so Undo state doesn't leak across runs.
                this._proofreadBeforeHtml = null;
                this.proofreadReviewVisible = false;
                this.proofreadingInProgress = false;
                this.cdr.markForCheck();
                return 0;
            }
            this.proofreadReviewLabel = count === 1
                ? '1 change to review'
                : count + ' changes to review';
        }
        this._proofreadBeforeHtml = beforeHtml || '';
        this.proofreadReviewVisible = true;
        this.proofreadingInProgress = false;
        this.cdr.markForCheck();
        return count;
    }

    /**
     * Counts the number of distinct change "blocks" between two HTML strings
     * at word granularity. A block is a contiguous run of inserted, deleted,
     * or substituted words — so "ths is a tst" → "this is a test" reports 2
     * changes (the two corrupted words), not 4 word-level operations.
     *
     * To avoid the inflated counts the previous version produced when the AI
     * subtly rewrote punctuation/casing/quotes throughout the note, tokens
     * are normalized aggressively before comparison:
     *   - HTML is parsed via DOM, then `textContent` is used (handles entities,
     *     hidden chars, etc. — far more robust than `<[^>]*>` regex stripping).
     *   - Unicode punctuation (smart quotes, em/en dashes, ellipses) is folded
     *     to ASCII equivalents.
     *   - Zero-width characters are stripped.
     *   - Tokens are lowercased and leading/trailing punctuation is removed,
     *     so "Hello," vs "hello" vs "Hello." all compare equal — the user
     *     doesn't perceive those as "changes" worth flagging.
     *
     * Algorithm: standard LCS DP over normalized tokens, then a single
     * backtrack counting contiguous non-match runs. O(n*m); bounded at
     * 62,500 cells with a cheap fallback so very long notes don't freeze.
     */
    private _countWordChanges(beforeHtml: string, afterHtml: string): number {
        let beforeTokens: string[] = this._extractNormalizedTokens(beforeHtml);
        let afterTokens: string[] = this._extractNormalizedTokens(afterHtml);
        let n: number = beforeTokens.length;
        let m: number = afterTokens.length;
        if (n === 0 && m === 0) {
            return 0;
        }
        // Fast path: identical token sequences mean nothing meaningful changed.
        if (n === m) {
            let identical: boolean = true;
            for (let k = 0; k < n; k++) {
                if (beforeTokens[k] !== afterTokens[k]) {
                    identical = false;
                    break;
                }
            }
            if (identical) {
                return 0;
            }
        }
        // Strip the common prefix + suffix so we only diff the part that
        // actually differs. Without this, a 1-word fix in the middle of a
        // 2,600-word note used to blow past the LCS cell budget and trigger
        // the heuristic fallback ("130 changes" instead of 1). With prefix/
        // suffix stripping, the middle is tiny and LCS is exact + cheap.
        let prefix: number = 0;
        let maxPrefix: number = Math.min(n, m);
        while (prefix < maxPrefix && beforeTokens[prefix] === afterTokens[prefix]) {
            prefix++;
        }
        let suffix: number = 0;
        let maxSuffix: number = Math.min(n - prefix, m - prefix);
        while (suffix < maxSuffix && beforeTokens[n - 1 - suffix] === afterTokens[m - 1 - suffix]) {
            suffix++;
        }
        let nMid: number = n - prefix - suffix;
        let mMid: number = m - prefix - suffix;
        if (nMid === 0 && mMid === 0) {
            return 0;
        }
        if (nMid === 0 || mMid === 0) {
            // Pure insertion (some words added) or pure deletion (some
            // removed) at one position — count it as a single change block.
            return 1;
        }
        // Slice once so the inner loops stay simple. Both arrays are short
        // after prefix/suffix trimming for realistic proofread scenarios.
        let beforeMid: string[] = beforeTokens.slice(prefix, n - suffix);
        let afterMid: string[] = afterTokens.slice(prefix, m - suffix);
        // Bound LCS table size — even after trimming, a worst-case full
        // rewrite would still produce a large middle. Fall back to a cheap
        // heuristic in that case so the UI never freezes.
        if (nMid * mMid > 250000) {
            let diff: number = Math.abs(nMid - mMid);
            return diff > 0 ? diff : Math.max(1, Math.floor(nMid * 0.05));
        }
        let dp: number[][] = [];
        for (let i = 0; i <= nMid; i++) {
            let row: number[] = new Array(mMid + 1).fill(0);
            dp.push(row);
        }
        for (let i = 1; i <= nMid; i++) {
            for (let j = 1; j <= mMid; j++) {
                if (beforeMid[i - 1] === afterMid[j - 1]) {
                    dp[i][j] = dp[i - 1][j - 1] + 1;
                } else {
                    dp[i][j] = dp[i - 1][j] >= dp[i][j - 1] ? dp[i - 1][j] : dp[i][j - 1];
                }
            }
        }
        // Backtrack — count contiguous runs of non-matches as "change blocks".
        let bi: number = nMid;
        let bj: number = mMid;
        let blocks: number = 0;
        let inChange: boolean = false;
        while (bi > 0 || bj > 0) {
            if (bi > 0 && bj > 0 && beforeMid[bi - 1] === afterMid[bj - 1]) {
                inChange = false;
                bi--;
                bj--;
            } else {
                if (!inChange) {
                    blocks++;
                    inChange = true;
                }
                if (bi === 0) {
                    bj--;
                } else if (bj === 0) {
                    bi--;
                } else if (dp[bi - 1][bj] >= dp[bi][bj - 1]) {
                    bi--;
                } else {
                    bj--;
                }
            }
        }
        return blocks;
    }

    /**
     * Parses HTML via DOM (so entities + nested tags are handled correctly),
     * normalizes Unicode oddities the AI sometimes introduces (smart quotes,
     * em/en dashes, ellipses, zero-width chars), then tokenizes into words.
     * Each token is lowercased and stripped of leading/trailing punctuation
     * so case + edge-punctuation differences don't count as "changes".
     */
    private _extractNormalizedTokens(html: string): string[] {
        if (!html) {
            return [];
        }
        let parser: HTMLDivElement = document.createElement('div');
        parser.innerHTML = html;
        let raw: string = (parser.textContent || '').toString();
        let normalized: string = raw
            // Smart / typographic quotes → straight ASCII equivalents.
            .replace(/[''‚‛′]/g, "'")
            .replace(/[“”„‟″]/g, '"')
            // Em-dash, en-dash, minus sign → ASCII hyphen.
            .replace(/[–—―−]/g, '-')
            // Ellipsis character → three dots.
            .replace(/…/g, '...')
            // Non-breaking spaces and friends → regular space.
            .replace(/[      ]/g, ' ')
            // Strip zero-width characters entirely.
            .replace(/[​‌‍⁠﻿]/g, '')
            // Collapse all whitespace runs to a single space.
            .replace(/\s+/g, ' ')
            .trim();
        if (!normalized) {
            return [];
        }
        // Tokenize on whitespace, then per-token strip leading/trailing
        // punctuation + lowercase so "Hello," == "hello" == "Hello." in the
        // comparison. Internal apostrophes/hyphens are kept so "don't" stays
        // a single token (and remains distinct from "do not").
        let pieces: string[] = normalized.split(' ');
        let result: string[] = [];
        for (let i = 0; i < pieces.length; i++) {
            let token: string = pieces[i].toLowerCase();
            // eslint-disable-next-line no-useless-escape
            token = token.replace(/^[^a-z0-9'\-]+/i, '').replace(/[^a-z0-9'\-]+$/i, '');
            if (token.length > 0) {
                result.push(token);
            }
        }
        return result;
    }

    /**
     * Retire the post-Proofread review pill because the user started typing again, so they don't have
     * to dismiss it by hand. The pre-Proofread snapshot goes with it: once there are fresh keystrokes
     * on top, "Undo all" would restore a state that never existed.
     */
    private _dismissProofreadReviewOnEdit(): void {
        if (!this.proofreadReviewVisible) {
            return;
        }
        this.proofreadReviewVisible = false;
        this._proofreadBeforeHtml = null;
        this.cdr.markForCheck();
    }

    /** Show the "Proofreading…" pill while the AI request is in flight. */
    public showProofreadProgress(): void {
        this.proofreadingInProgress = true;
        this.proofreadReviewVisible = false;
        this.cdr.markForCheck();
    }

    /** Hide the in-progress pill — called when the AI request fails so the
     *  user isn't left looking at a permanent spinner. */
    public hideProofreadProgress(): void {
        this.proofreadingInProgress = false;
        this.cdr.markForCheck();
    }

    /** Dismiss the pill — Proofread changes are kept as-is. */
    public acceptProofreadReview(): void {
        this._proofreadBeforeHtml = null;
        this.proofreadReviewVisible = false;
        this.cdr.markForCheck();
    }

    /** Restore the pre-Proofread snapshot and dismiss the pill. */
    public undoProofreadReview(): void {
        if (this._proofreadBeforeHtml !== null) {
            this.setValue(this._proofreadBeforeHtml);
            // Emit so hosts persisting the value receive the rollback.
            this.ngZone.run(() => {
                this.valueChange.emit({ value: this._proofreadBeforeHtml || '', bubbles: false });
            });
        }
        this._proofreadBeforeHtml = null;
        this.proofreadReviewVisible = false;
        this.cdr.markForCheck();
    }

    /** Pill "Accept all" click handler — clears the snapshot and notifies the host. */
    public onProofreadAcceptClick(): void {
        this.acceptProofreadReview();
        this.proofreadReviewAccept.emit();
    }

    /** Pill "Undo all" click handler — restores the snapshot, dismisses the
     *  pill, and notifies the host (in addition to the valueChange the
     *  restore already fires). */
    public onProofreadUndoClick(): void {
        this.undoProofreadReview();
        this.proofreadReviewUndo.emit();
    }

    public undo(): void {
        this.executeCommand('undo');
    }

    public redo(): void {
        this.executeCommand('redo');
    }

    // --- Slash menu UI ---

    /**
     * Scrolls the currently highlighted slash-menu row into view inside the
     * popup's scroll container. Runs after the next paint so the DOM reflects
     * the latest `slashSelectedIndex`. Works for both the grouped layout
     * (`.vf-tiptap-mention-row`) and the legacy flat list (`.vf-tiptap-slash-item`).
     */
    private _scrollSelectedSlashRowIntoView(): void {
        const popup: HTMLElement | null = this.slashPopup ? this.slashPopup.nativeElement : null;
        if (!popup) {
            return;
        }
        setTimeout(() => {
            const selected: HTMLElement | null = popup.querySelector(
                '.vf-tiptap-mention-row.is-selected, .vf-tiptap-slash-item.is-selected'
            );
            if (!selected) {
                return;
            }
            if (typeof selected.scrollIntoView === 'function') {
                selected.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            }
        }, 0);
    }

    public selectSlashItem(item: SlashMenuItem, index: number): void {
        this.slashSelectedIndex = index;
        // Delete the trigger char + query BEFORE emitting so the host's
        // select handler (which typically inserts the mention at the cursor)
        // lands at the right position. The suggestion plugin already does
        // this for Enter/Tab via its `command` callback; clicks bypass that
        // path so we replicate it here using the captured range.
        if (this.editor && this._currentSlashRange) {
            this.editor.chain().focus().deleteRange(this._currentSlashRange).run();
        }
        // Route to whichever trigger opened the popup. Enter/Tab already do this — they run through the
        // owning plugin's own onSelect — but a CLICK bypasses the plugin entirely and lands here, so
        // without this the secondary menu's picks would be reported as primary ones.
        const args = { item: { innerText: item.text }, data: item };
        if (this._secondaryMenuActive) {
            this.secondaryMenuItemSelect.emit(args);
        } else {
            this.slashMenuItemSelect.emit(args);
        }
        this.slashVisible = false;
        this.slashStyle = { display: 'none' };
        this._currentSlashRange = null;
        this._syncSlashAccessibility();
        this.cdr.markForCheck();
    }

    /** Which trigger owns the currently-open popup (see selectSlashItem). */
    private _secondaryMenuActive = false;

    /** Rows are tracked by the item itself (what Angular does by default), so the id a row carries moves with it. */
    public trackBySlashItem(_index: number, item: SlashMenuItem): SlashMenuItem {
        return item;
    }

    /**
     * The attributes of the editing area (CO-09). `role="textbox"` is tiptap's own default, said again here because a later
     * `setProps` replaces the whole attributes object and would drop it. Then a multi-line flag, the host's accessible name and,
     * while the grouped picker is on, the id of the listbox it opens. `aria-activedescendant` changes with the highlight, so it is
     * written on its own (see _syncSlashAccessibility). A host that sets neither `ariaLabel` nor the grouped picker gets only the
     * role and the multi-line flag.
     */
    private _editorAriaAttributes(): { [name: string]: string } {
        const attributes: { [name: string]: string } = { role: 'textbox', 'aria-multiline': 'true' };

        if (this.ariaLabel) {
            attributes['aria-label'] = this.ariaLabel;
        }
        if (this.showSlashMenu && this.useGroupedSlashLayout) {
            attributes['aria-controls'] = this.slashListId;
        }
        return attributes;
    }

    /** True while the open list is the list of teammates: the grouped picker opened by '@' (the comment composer's primary trigger). */
    private _isPeopleList(): boolean {
        return this.useGroupedSlashLayout && this.slashTriggerChar === '@' && !this._secondaryMenuActive;
    }

    /**
     * Brings the screen-reader view of the open list in step with it (CO-09): the editing area's `aria-activedescendant` names the
     * highlighted option of the grouped list (and is removed when no list is on view), and the count of the people list is
     * announced once it has settled. Called wherever the items, the highlight or the visibility change.
     */
    private _syncSlashAccessibility(): void {
        const dom: HTMLElement | null = this.editor ? (this.editor.view.dom as HTMLElement) : null;
        const selected: SlashMenuItem | undefined = this.slashItems[this.slashSelectedIndex];
        const optionId: string = this.useGroupedSlashLayout && this.slashVisible && selected ? selected['optionId'] || '' : '';

        if (dom) {
            if (optionId) {
                dom.setAttribute('aria-activedescendant', optionId);
            } else {
                dom.removeAttribute('aria-activedescendant');
            }
        }
        this.slashShowsNoMatch =
            this.slashVisible && this._isPeopleList() && this._isSlashSettled() && !this.slashItems.length && !!this.slashQuery;
        this._scheduleSlashStatus();
    }

    /** The list on view answers the current query: the bare '@' (answered from the preloaded teammates) or a query the host has answered. */
    private _isSlashSettled(): boolean {
        return !this.slashQuery || this._slashAnswered;
    }

    /**
     * The people-list count, spoken after the list has stayed as it is for a moment (SLASH_STATUS_DELAY_MS), so typing "jan" does not
     * announce for j, ja and jan. A closed list, or any list but the teammates, says nothing; so does a query the host has not
     * answered yet (the rows on view are the last query's), and the bare '@' says nothing until its first rows have arrived (an
     * empty list with no query is "not loaded yet", not "no match").
     */
    private _scheduleSlashStatus(): void {
        if (this._slashStatusTimer !== null) {
            window.clearTimeout(this._slashStatusTimer);
            this._slashStatusTimer = null;
        }
        if (!this.slashVisible || !this._isPeopleList()) {
            this._setSlashStatus('');
            return;
        }
        this._slashStatusTimer = window.setTimeout(() => {
            const count: number = this.slashItems.length;

            this._slashStatusTimer = null;
            if (!this._isSlashSettled() || (!count && !this.slashQuery)) {
                this._setSlashStatus('');
                return;
            }
            this._setSlashStatus(count === 1 ? '1 person matches' : count + ' people match');
        }, SLASH_STATUS_DELAY_MS);
    }

    private _setSlashStatus(text: string): void {
        if (this.slashStatusText !== text) {
            this.slashStatusText = text;
            this.cdr.markForCheck();
        }
    }

    // --- Internals ---

    private _buildExtensions(): any[] {
        const exts: any[] = [
            StarterKit.configure({
                history: { depth: 100 },
                heading: { levels: [1, 2, 3] },
                // Suppress ProseMirror's drop-position cursor (a thin line). Our
                // file-paste/drop flow renders its own centered "Drop to attach"
                // card via the FileDropHandler extension, so the extra line is
                // redundant noise.
                dropcursor: false
            }),
            Underline,
            // Renders plain hosted <img> from email content (e.g. the "Original email" section of an
            // email-derived task). StarterKit omits an image node and @tiptap/extension-image isn't
            // installed, so without this the email's images are stripped on parse.
            InlineImage,
            // `title` is added to the Link mark's schema so a hover tooltip survives parse/serialize
            // (the base mark allows only href/target/rel/class and silently drops it). Additive — links
            // without a title behave exactly as before. Used by the reply composer's inline attachments
            // to expose each file's sharing scope / permission / expiry on hover.
            Link.extend({
                addAttributes() {
                    return {
                        ...(this.parent?.() || {}),
                        title: {
                            default: null,
                            parseHTML: (element: HTMLElement) => element.getAttribute('title'),
                            renderHTML: (attributes: { title?: string }) =>
                                attributes.title ? { title: attributes.title } : {}
                        },
                        // Allow the app's CSS tooltip (dark styled, same as the collapsed main menu) to
                        // survive parse/serialize on a link. Inert in the sent email — pure CSS.
                        class: {
                            default: null,
                            parseHTML: (element: HTMLElement) => element.getAttribute('class'),
                            renderHTML: (attributes: { class?: string }) =>
                                attributes.class ? { class: attributes.class } : {}
                        },
                        'data-tooltip': {
                            default: null,
                            parseHTML: (element: HTMLElement) => element.getAttribute('data-tooltip'),
                            renderHTML: (attributes: { 'data-tooltip'?: string }) =>
                                attributes['data-tooltip'] ? { 'data-tooltip': attributes['data-tooltip'] } : {}
                        }
                    };
                }
            }).configure({
                openOnClick: false,
                autolink: true,
                HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' }
            }),
            Placeholder.configure({
                placeholder: this.placeholder || ''
            }),
            CharacterCount.configure({
                limit: this.maxLength || null
            }),
            TextStyle,
            Color,
            Highlight.configure({ multicolor: true }),
            FontFamily,
            FontSize,
            ParagraphIndent,
            CommentAnchor,
            TaskList,
            TaskItem.configure({ nested: true }),
            Table.configure({ resizable: true, allowTableNodeSelection: true, HTMLAttributes: { class: 'vf-tiptap-table' } }),
            TableRow,
            TableHeader,
            TableCell,
            // One node for BOTH insertion paths (upload + "/" search). Its own
            // ProseMirror plugin owns click routing, so these callbacks are reached
            // from live document attrs and survive every editor re-render.
            ReferenceToken.configure({
                HTMLAttributes: {},
                onImageClick: (attrs: ReferenceTokenAttrs) => {
                    this.ngZone.run(() => this._openTokenImage(attrs));
                },
                onFileClick: (attrs: ReferenceTokenAttrs) => {
                    this.ngZone.run(() => this._openTokenFile(attrs));
                },
                onFolderClick: (attrs: ReferenceTokenAttrs) => {
                    this.ngZone.run(() => this._openTokenFolder(attrs));
                },
                onTaskClick: (attrs: ReferenceTokenAttrs) => {
                    this.ngZone.run(() => this._invokeMentionCallback('task', tokenAttrsToMentionAttrs(attrs)));
                },
                onClientClick: (attrs: ReferenceTokenAttrs) => {
                    this.ngZone.run(() => this._invokeMentionCallback('client', tokenAttrsToMentionAttrs(attrs)));
                }
            })
        ];

        if (!this.disableImageUpload) {
            exts.push(
                FileDropHandler.configure({
                    maxFileSizeBytes: DEFAULT_MAX_UPLOAD_BYTES,
                    onFileDropped: (detail: FileDropDetail) => {
                        this.ngZone.run(() => this._handleFileDropped(detail));
                    },
                    onReject: (detail: FileDropRejectDetail) => {
                        this.ngZone.run(() => this._handleFileReject(detail));
                    },
                    // 'host' mode takes every file of the paste or drop (read at drop time); 'inline' keeps the one-file flow.
                    onFilesDropped: (files: File[], source: 'paste' | 'drop'): boolean => {
                        if (!handsFileDropToHost(this.fileDropMode)) {
                            return false;
                        }
                        this.ngZone.run(() => this._handFilesToHost(files, source));
                        return true;
                    }
                })
            );
        }

        if (this.showSlashMenu) {
            exts.push(
                SlashMenu.configure({
                    char: this.slashTriggerChar || '/',
                    allowSpaces: this.slashAllowSpaces !== false,
                    onStateChange: (state) => this._applySlashState(state, false),
                    onQueryChange: (query) => {
                        this.ngZone.run(() => {
                            this.slashQueryChange.emit(query);
                        });
                    },
                    getItems: () => this._slashItemsBuffer,
                    onSelect: (item) => {
                        this.ngZone.run(() => {
                            this.slashMenuItemSelect.emit({ item: { innerText: item.text }, data: item });
                        });
                    }
                })
            );

            // Second trigger (opt-in). Shares the popup and the item buffer — only one menu can be
            // open at a time — but reports through its own outputs so the host knows which fired.
            if (this.secondaryTriggerChar) {
                exts.push(
                    SecondarySlashMenu.configure({
                        char: this.secondaryTriggerChar,
                        allowSpaces: this.secondaryAllowSpaces !== false,
                        onStateChange: (state) => this._applySlashState(state, true),
                        onQueryChange: (query) => {
                            this.ngZone.run(() => {
                                this.secondaryQueryChange.emit(query);
                            });
                        },
                        getItems: () => this._slashItemsBuffer,
                        onSelect: (item) => {
                            this.ngZone.run(() => {
                                this.secondaryMenuItemSelect.emit({ item: { innerText: item.text }, data: item });
                            });
                        }
                    })
                );
            }
        }

        return exts;
    }

    /** Popup state, shared by both trigger plugins (see `secondaryTriggerChar`). */
    private _applySlashState(state: SlashSuggestionState, isSecondary: boolean): void {
        this.ngZone.run(() => {
            // Only claim ownership while OPEN: the closing plugin must not relabel the popup that the
            // other trigger may have just opened.
            if (state.visible) {
                // Switching triggers must drop the other trigger's rows, or the popup shows the last
                // `@` teammates for a beat under the `/` header while the file search is in flight.
                if (this._secondaryMenuActive !== isSecondary) {
                    this._slashItemsBuffer = [];
                }
                this._secondaryMenuActive = isSecondary;
            }
            this.slashVisible = state.visible;
            this._currentSlashRange = state.range;
            // A new query is not answered until the host sets `slashMenuItems` again (CO-09).
            if (!state.visible || (state.query || '') !== this.slashQuery) {
                this._slashAnswered = false;
            }
            this.slashQuery = state.query || '';
            // If caller has populated items via setter, prefer those.
            this.slashItems = this._slashItemsBuffer.length
                ? this._slashItemsBuffer
                : this._trustRowIcons(state.items);
            this.slashSelectedIndex = state.selectedIndex;
            this._syncSlashAccessibility();
            if (state.visible && state.clientRect) {
                this.slashStyle = {
                    display: 'block',
                    position: 'fixed',
                    top: state.clientRect.bottom + 4 + 'px',
                    left: state.clientRect.left + 'px'
                };
            } else {
                this.slashStyle = { display: 'none' };
            }
            this.cdr.markForCheck();
            // After Angular re-renders, scroll the highlighted row into view so arrow-key nav past the
            // visible area keeps the selection on screen.
            if (state.visible) {
                this._scrollSelectedSlashRowIntoView();
            }
        });
    }

    private _runIndent(): void {
        if (!this.editor) {
            return;
        }
        if (this.editor.isActive('taskItem')) {
            this.editor.chain().focus().sinkListItem('taskItem').run();
            return;
        }
        const inList = this.editor.isActive('listItem') || this.editor.isActive('orderedList') || this.editor.isActive('bulletList');
        if (inList) {
            this.editor.chain().focus().sinkListItem('listItem').run();
        } else {
            (this.editor.chain().focus() as any).indentBlock().run();
        }
    }

    private _runOutdent(): void {
        if (!this.editor) {
            return;
        }
        if (this.editor.isActive('taskItem')) {
            this.editor.chain().focus().liftListItem('taskItem').run();
            return;
        }
        const inList = this.editor.isActive('listItem') || this.editor.isActive('orderedList') || this.editor.isActive('bulletList');
        if (inList) {
            this.editor.chain().focus().liftListItem('listItem').run();
        } else {
            (this.editor.chain().focus() as any).outdentBlock().run();
        }
    }

    private _updateBubbleMenuPosition(): void {
        if (!this.showBubbleMenu || !this.editor) {
            return;
        }
        const { state } = this.editor;
        const { from, to, empty } = state.selection;
        // Hide bubble menu when selection is on an atom/file-card node — inline
        // formatting doesn't apply to image cards or other non-text blocks.
        if (this.editor.isActive('imageFileCard')) {
            this.bubbleVisible = false;
            this.bubbleStyle = { display: 'none' };
            this.showFontColorPicker = false;
            this.showHighlightPicker = false;
            this.showFontSizeMenu = false;
            this.ngZone.run(() => this.cdr.markForCheck());
            return;
        }
        if (empty || this.readonly) {
            this.bubbleVisible = false;
            this.bubbleStyle = { display: 'none' };
            this.showFontColorPicker = false;
            this.showHighlightPicker = false;
            this.showFontSizeMenu = false;
            this.ngZone.run(() => this.cdr.markForCheck());
            return;
        }
        try {
            const wrapperEl = this.host.nativeElement.querySelector('.vf-tiptap-wrapper') as HTMLElement;
            const refRect = (wrapperEl || this.host.nativeElement).getBoundingClientRect();
            const toolbarEl = this.host.nativeElement.querySelector('.vf-tiptap-toolbar') as HTMLElement;
            const toolbarHeight = toolbarEl ? toolbarEl.offsetHeight : 0;
            // VIEWPORT coordinates, because the bubble is re-parented to <body> and positioned `fixed`.
            // It has to be: the editor sits inside a scrolling pane (`.tdb-scroll`, overflow:auto) which
            // CLIPS an absolutely-positioned child, so the design's "hangs past the modal edge" is
            // unreachable from inside the pane — the pill was being sliced in half at the boundary.
            const start = this.editor.view.coordsAtPos(from);
            const end = this.editor.view.coordsAtPos(to);
            const rawCenterX = (start.left + end.left) / 2;
            const bubbleHeight = 40;
            const gap = 8;
            const minTop = refRect.top + toolbarHeight + 4;
            const aboveTop = start.top - bubbleHeight - gap;
            const finalTop = aboveTop >= minTop ? aboveTop : start.bottom + gap;
            // Centred on the selection, NOT clamped to the editor: per design the bubble may hang past
            // the modal edge (it is a floating surface over the app, not a child of the pane). The
            // post-render pass below keeps it inside the VIEWPORT, which is the only real boundary.
            const centerX = rawCenterX;
            this.bubbleVisible = true;
            this.bubbleStyle = {
                display: 'inline-flex',
                position: 'fixed',
                top: finalTop + 'px',
                left: centerX + 'px',
                transform: 'translateX(-50%)'
            };
            this.ngZone.run(() => this.cdr.markForCheck());
            // Measure the actual rendered bubble and nudge it if it's still clipping.
            setTimeout(() => this._adjustBubblePosition(), 0);
        } catch {
            this.bubbleVisible = false;
            this.bubbleStyle = { display: 'none' };
        }
    }

    private _adjustBubblePosition(): void {
        if (!this.bubbleVisible || !this.bubbleMenuEl?.nativeElement) {
            return;
        }
        const bubbleEl = this.bubbleMenuEl.nativeElement;
        const wrapperEl = this.host.nativeElement.querySelector('.vf-tiptap-wrapper') as HTMLElement;
        if (!wrapperEl) {
            return;
        }
        const wrapperRect = wrapperEl.getBoundingClientRect();
        const bubbleRect = bubbleEl.getBoundingClientRect();
        const margin = 8;

        // Clamp to the VIEWPORT, not the editor wrapper: the design lets the bubble hang past the modal
        // edge over whatever is behind it, so the pane is not a boundary — the window is.
        let dx = 0;
        const leftOverflow = margin - bubbleRect.left;
        const rightOverflow = bubbleRect.right - (window.innerWidth - margin);
        if (leftOverflow > 0) {
            dx = leftOverflow;
        } else if (rightOverflow > 0) {
            dx = -rightOverflow;
        }

        // Vertical correction — re-decide above/below using the *measured* bubble height
        // (the initial estimate is 40px but a wrapped bubble can be much taller).
        let dy = 0;
        if (this.editor) {
            try {
                const { from } = this.editor.state.selection;
                const start = this.editor.view.coordsAtPos(from);
                const toolbarEl = this.host.nativeElement.querySelector('.vf-tiptap-toolbar') as HTMLElement;
                const toolbarHeight = toolbarEl ? toolbarEl.offsetHeight : 0;
                const minTop = wrapperRect.top + toolbarHeight + 4;
                const gap = 8;
                const aboveTopViewport = start.top - bubbleRect.height - gap;
                const belowTopViewport = start.bottom + gap;
                const desiredTopViewport = aboveTopViewport >= minTop ? aboveTopViewport : belowTopViewport;
                dy = desiredTopViewport - bubbleRect.top;
            } catch {
                dy = 0;
            }
        }

        if (dx !== 0 || dy !== 0) {
            const currentLeft = parseFloat(((this.bubbleStyle?.left as string) || '0').replace('px', '')) || 0;
            const currentTop = parseFloat(((this.bubbleStyle?.top as string) || '0').replace('px', '')) || 0;
            const next: any = { ...this.bubbleStyle };
            if (dx !== 0) {
                next.left = (currentLeft + dx) + 'px';
            }
            if (dy !== 0) {
                next.top = (currentTop + dy) + 'px';
            }
            this.bubbleStyle = next;
            this.ngZone.run(() => this.cdr.markForCheck());
        }
    }

    private _hoveredAnchorId: string | null = null;
    /** Delegated hover over the notes' anchor highlights → emit the anchor id (or null on leave). Opt-in via
     *  showNewComment so non-anchor editors are unaffected; change-tracked so it fires once per anchor, not
     *  per pointer move. */
    private _onDomAnchorHover = (event: Event): void => {
        if (!this.showNewComment) {
            return;
        }
        const target = event.target as HTMLElement | null;
        const span = (target && target.closest ? target.closest('span[data-comment-id]') : null) as HTMLElement | null;
        const id = span ? span.getAttribute('data-comment-id') : null;
        if (id !== this._hoveredAnchorId) {
            this._hoveredAnchorId = id;
            this.ngZone.run(() => this.anchorHover.emit(id && span ? { id, el: span } : null));
        }
    };

    private _onDomAnchorLeave = (): void => {
        if (!this.showNewComment || this._hoveredAnchorId === null) {
            return;
        }
        this._hoveredAnchorId = null;
        this.ngZone.run(() => this.anchorHover.emit(null));
    };

    /** Delegated click on an anchor highlight → emit its id so the host can jump the stream to the matching
     *  comment (spec §5). Not preventDefault-ed — the caret still places, editing is unaffected. Only real
     *  `c{id}` anchors emit (a provisional `pending-…` highlight has no stream entry yet). */
    private _onDomAnchorClick = (event: Event): void => {
        if (!this.showNewComment) {
            return;
        }
        const target = event.target as HTMLElement | null;
        const span = target && target.closest ? target.closest('span[data-comment-id]') : null;
        const id = span ? span.getAttribute('data-comment-id') : null;
        if (id && id.charAt(0) === 'c') {
            this.ngZone.run(() => this.anchorClick.emit(id));
        }
    };

    private _onDomKeyUp = (ev: KeyboardEvent): void => {
        this.ngZone.run(() => {
            this.keyUp.emit({
                type: 'keyup',
                key: ev.key,
                code: ev.code,
                originalEvent: ev
            });
        });
    };

    private _onDomBlur = (): void => {
        // Delay to allow clicks on bubble menu/slash popup
        setTimeout(() => {
            this.bubbleVisible = false;
            this.bubbleStyle = { display: 'none' };
            this.cdr.markForCheck();
        }, 150);
    };

    /**
     * Global mousedown handler — dismisses the slash menu when the user
     * clicks anywhere outside the editor host AND outside the slash popup.
     * The `/` text + any typed query stay as plain text (we just terminate
     * the suggestion plugin's session by dispatching an Escape on the
     * editor's DOM, which routes through the plugin's own onKeyDown).
     *
     * Also closes any open toolbar dropdown (Write-with-AI menu, color
     * picker, highlight picker, font-size menu, table menu) when the user
     * clicks outside the toolbar.
     */
    private _onDocumentMouseDown = (ev: MouseEvent): void => {
        const target = ev.target as Node | null;
        if (!target) {
            return;
        }
        const popupEl = this.slashPopup ? this.slashPopup.nativeElement : null;
        const editorDom = this.editor ? (this.editor.view.dom as HTMLElement) : null;
        const toolbarEl = this.host.nativeElement.querySelector('.vf-tiptap-toolbar') as HTMLElement | null;

        // Close any open toolbar dropdown when the click lands outside the
        // toolbar itself. Each dropdown is rendered as a sibling of its
        // trigger button inside the toolbar, so any click outside the
        // toolbar should dismiss them.
        const clickedInsideToolbar: boolean = toolbarEl ? toolbarEl.contains(target) : false;
        const anyToolbarMenuOpen: boolean =
            this.showTbWriteAiMenu
            || this.showTbColorPicker
            || this.showTbHighlightPicker
            || this.showTbFontSizeMenu
            || this.showTbTableMenu;
        if (!clickedInsideToolbar && anyToolbarMenuOpen) {
            this.ngZone.run(() => {
                this.showTbWriteAiMenu = false;
                this.showTbColorPicker = false;
                this.showTbHighlightPicker = false;
                this.showTbFontSizeMenu = false;
                this.showTbTableMenu = false;
                this.cdr.markForCheck();
            });
        }

        // Slash-menu dismissal — only run when the slash menu is currently
        // visible (cheap exit otherwise).
        if (!this.slashVisible) {
            return;
        }
        if (popupEl && popupEl.contains(target)) {
            return;
        }
        if (editorDom && editorDom.contains(target)) {
            return;
        }
        // Outside click — forward an Escape keydown to the editor so the
        // Suggestion plugin's onKeyDown fires onExit and hides the menu.
        if (editorDom) {
            const escEvent: KeyboardEvent = new KeyboardEvent('keydown', {
                key: 'Escape',
                code: 'Escape',
                keyCode: 27,
                which: 27,
                bubbles: true,
                cancelable: true
            } as KeyboardEventInit);
            editorDom.dispatchEvent(escEvent);
        }
        // Local fallback — if the suggestion plugin's exit hook didn't fire
        // (e.g. the editor was already blurred), force-hide the popup so
        // the dropdown doesn't persist on the screen.
        this.ngZone.run(() => {
            this.slashVisible = false;
            this.slashStyle = { display: 'none' };
            this.slashItems = [];
            this._slashItemsBuffer = [];
            this._syncSlashAccessibility();
            this.cdr.markForCheck();
        });
    };

    private _updateLinkPopover(): void {
        if (!this.editor || this.readonly) {
            this.linkPopoverVisible = false;
            this.linkPopoverStyle = { display: 'none' };
            this.ngZone.run(() => this.cdr.markForCheck());
            return;
        }
        const isLink = this.editor.isActive('link');
        if (!isLink) {
            this.linkPopoverVisible = false;
            this.linkPopoverStyle = { display: 'none' };
            this.ngZone.run(() => this.cdr.markForCheck());
            return;
        }
        try {
            const attrs = this.editor.getAttributes('link');
            this.currentLinkHref = attrs?.href || '';
            // A person chip (CO-09) is a link in the document but not one to visit, edit or remove: no popover for it.
            if (this.currentLinkHref.indexOf(MENTION_LINK_FRAGMENT) >= 0) {
                this.linkPopoverVisible = false;
                this.linkPopoverStyle = { display: 'none' };
                this.ngZone.run(() => this.cdr.markForCheck());
                return;
            }
            const wrapperEl = this.host.nativeElement.querySelector('.vf-tiptap-wrapper') as HTMLElement;
            const refRect = (wrapperEl || this.host.nativeElement).getBoundingClientRect();
            const { from } = this.editor.state.selection;
            const start = this.editor.view.coordsAtPos(from);
            const left = start.left - refRect.left;
            const top = start.bottom - refRect.top;
            this.linkPopoverVisible = true;
            this.linkPopoverStyle = {
                position: 'absolute',
                top: (top + 6) + 'px',
                left: Math.max(left, 4) + 'px'
            };
            this.ngZone.run(() => this.cdr.markForCheck());
            setTimeout(() => this._adjustLinkPopoverPosition(), 0);
        } catch {
            this.linkPopoverVisible = false;
            this.linkPopoverStyle = { display: 'none' };
        }
    }

    private _adjustLinkPopoverPosition(): void {
        if (!this.linkPopoverVisible || !this.linkPopoverEl?.nativeElement) {
            return;
        }
        const popEl = this.linkPopoverEl.nativeElement;
        const wrapperEl = this.host.nativeElement.querySelector('.vf-tiptap-wrapper') as HTMLElement;
        if (!wrapperEl) {
            return;
        }
        const wrapperRect = wrapperEl.getBoundingClientRect();
        const popRect = popEl.getBoundingClientRect();
        const margin = 8;
        const leftOverflow = (wrapperRect.left + margin) - popRect.left;
        const rightOverflow = popRect.right - (wrapperRect.right - margin);
        let dx = 0;
        if (leftOverflow > 0) {
            dx = leftOverflow;
        } else if (rightOverflow > 0) {
            dx = -rightOverflow;
        }
        if (dx !== 0) {
            const currentLeft = parseFloat(((this.linkPopoverStyle?.left as string) || '0').replace('px', '')) || 0;
            this.linkPopoverStyle = { ...this.linkPopoverStyle, left: (currentLeft + dx) + 'px' };
            this.ngZone.run(() => this.cdr.markForCheck());
        }
    }

    public visitCurrentLink(): void {
        if (this.currentLinkHref) {
            window.open(this.currentLinkHref, '_blank', 'noopener,noreferrer');
        }
    }

    public editCurrentLink(): void {
        if (!this.editor) {
            return;
        }
        this.openLinkInputPopup('edit', this.currentLinkHref || '');
    }

    public openLinkInputPopup(mode: 'add' | 'edit', currentUrl: string): void {
        this.linkInputMode = mode;
        this.linkInputUrl = currentUrl || '';
        this.showLinkInputPopup = true;
        this.bubbleVisible = false;
        this.linkPopoverVisible = false;
        this.cdr.markForCheck();
        setTimeout(() => {
            const el = this.linkInputField?.nativeElement;
            if (el) {
                el.focus();
                el.select();
            }
        }, 0);
    }

    public confirmLinkInput(): void {
        if (!this.editor) {
            this.showLinkInputPopup = false;
            return;
        }
        const url = (this.linkInputUrl || '').trim();
        const mode = this.linkInputMode;
        this.showLinkInputPopup = false;
        if (mode === 'edit' && url === '') {
            this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
            this._updateLinkPopover();
            return;
        }
        if (!url) {
            this.editor.commands.focus();
            return;
        }
        this.editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run();
        if (mode === 'edit') {
            this._updateLinkPopover();
        }
    }

    public cancelLinkInput(): void {
        this.showLinkInputPopup = false;
        if (this.editor) {
            this.editor.commands.focus();
        }
    }

    public unlinkCurrent(): void {
        if (!this.editor) {
            return;
        }
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
        this.linkPopoverVisible = false;
        this.cdr.markForCheck();
    }

    // --- Shell-wide file drop zone ---

    /**
     * True while a FILE drag is anywhere over the editor SHELL — content, toolbar
     * or footer. Drives the dashed outline + "Drop to attach" card.
     *
     * The ProseMirror plugin only sees drags over its own element (`.ProseMirror`),
     * so the toolbar and footer strips were dead zones: releasing a file there fell
     * through to the browser default and opened the file in a new tab. These
     * handlers sit on the wrapper, which every inner drag event bubbles up to, so
     * the whole control is one target.
     */
    public isFileDragOver: boolean = false;

    private _shellEl(): HTMLElement | null {
        return this.host.nativeElement.querySelector('.vf-tiptap-wrapper');
    }

    /**
     * Lets a HOST that owns chrome OUTSIDE this component light up the same drop
     * affordance. Needed because some surfaces render their own footer/header
     * around the editor — the redesign's `vf-task-notes` puts the
     * "0 / 50000 · Saved" row in its own `.tn-foot`, outside our wrapper — so a
     * drag over that strip can't be seen by our own handlers. The host pairs this
     * with `ingestExternalFile()` on drop.
     */
    public setFileDragOver(on: boolean): void {
        if (this.disableImageUpload || this.isFileDragOver === on) {
            return;
        }
        this.isFileDragOver = on;
        this.cdr.markForCheck();
    }

    public onShellDragEnter(ev: DragEvent): void {
        if (this.disableImageUpload || !dataTransferHasFiles(ev.dataTransfer)) {
            return;
        }
        this.isFileDragOver = true;
        this.cdr.markForCheck();
    }

    public onShellDragOver(ev: DragEvent): void {
        if (this.disableImageUpload || !dataTransferHasFiles(ev.dataTransfer)) {
            return;
        }
        // Required on EVERY dragover — without it the browser treats the element as
        // a non-target and the drop never fires.
        ev.preventDefault();
        if (!this.isFileDragOver) {
            this.isFileDragOver = true;
            this.cdr.markForCheck();
        }
    }

    public onShellDragLeave(ev: DragEvent): void {
        const related: Node | null = ev.relatedTarget as Node | null;
        const shell: HTMLElement | null = this._shellEl();

        // dragleave also fires when crossing between children (toolbar → content →
        // footer). Only clear once the pointer has actually left the shell.
        if (related && shell && shell.contains(related)) {
            return;
        }
        if (!this.isFileDragOver) {
            return;
        }
        this.isFileDragOver = false;
        this.cdr.markForCheck();
    }

    public onShellDrop(ev: DragEvent): void {
        const dt: DataTransfer | null = ev.dataTransfer;

        if (this.isFileDragOver) {
            this.isFileDragOver = false;
            this.cdr.markForCheck();
        }
        if (this.disableImageUpload) {
            return;
        }
        // A drop that landed inside .ProseMirror was already taken by the
        // FileDropHandler plugin (which calls preventDefault) — bailing here keeps
        // it from being uploaded twice. What reaches this point is a drop on the
        // toolbar/footer chrome, which has no cursor position, so it inserts at the
        // current selection.
        if (ev.defaultPrevented) {
            return;
        }
        if (!dt || !dt.files || dt.files.length === 0) {
            return;
        }
        ev.preventDefault();
        this.ingestExternalFiles(dt.files);
    }

    // --- File paste/drop + lightbox handlers ---

    /**
     * Programmatic entry point for "this file was dropped on the composer".
     * The ProseMirror plugin only sees drops that land on its own DOM element
     * (`view.dom` = .ProseMirror), so when the composer chrome around the
     * editor (e.g. a bottom action row with paperclip / @ / Proofread / Send
     * buttons sitting BELOW the editor host) is part of the visible composer,
     * drops on that chrome would otherwise fall through to the browser
     * default (open the file in a new tab). Consumers wire (dragover)+(drop)
     * on the outer composer container and call this — it mirrors the same
     * validation + upload-modal hand-off the in-editor plugin performs.
     */
    public ingestExternalFile(file: File, position: number | null = null): void {
        const source: 'paste' | 'drop' = 'drop';

        if (this.disableImageUpload || !file) {
            return;
        }
        if (!file.size) {
            this._handleFileReject({ reason: 'empty', fileName: file.name || '', fileSize: 0, source });
            return;
        }
        if (file.size > DEFAULT_MAX_UPLOAD_BYTES) {
            this._handleFileReject({ reason: 'too-large', fileName: file.name || '', fileSize: file.size, source });
            return;
        }
        this._handleFileDropped({ file, position, source });
    }

    /**
     * `ingestExternalFile` for EVERY file of a drop on chrome outside the editor (the shell's own toolbar/footer, or a host's
     * card: comment-composer `onCardDrop`). `fileDropMode` 'host': all of them reach the host in one `(fileDrop)`. 'inline': the
     * first one, as always (the upload modal takes one file).
     */
    public ingestExternalFiles(files: ArrayLike<File> | null | undefined): void {
        if (this.disableImageUpload || !files || files.length === 0) {
            return;
        }
        if (handsFileDropToHost(this.fileDropMode)) {
            this._handFilesToHost(Array.from(files), 'drop');
            return;
        }
        this.ingestExternalFile(files[0], null);
    }

    /**
     * `fileDropMode` 'host': every file of one paste or drop, screened with the editor's own checks (`splitHostFileDrop`; a toast
     * per refused file, the same as for one), pasted images named as always, then all the accepted ones in ONE `(fileDrop)` so
     * the host opens one upload step for them. Nothing is uploaded or inserted here.
     */
    private _handFilesToHost(files: File[], source: 'paste' | 'drop'): void {
        const split: HostFileDropSplit<File> = splitHostFileDrop<File>(files, DEFAULT_MAX_UPLOAD_BYTES);
        const named: File[] = split.accepted.map((file: File) => this._namePastedImage(file, source));

        split.refused.forEach((refusal: { file: File; reason: HostFileDropRefusal }) =>
            this._handleFileReject({
                reason: refusal.reason,
                fileName: refusal.file.name || '',
                fileSize: refusal.file.size || 0,
                source
            })
        );
        if (named.length > 0) {
            this.fileDrop.emit(named);
        }
    }

    private _handleFileDropped(detail: FileDropDetail): void {
        const file: File = this._namePastedImage(detail.file, detail.source);

        // `fileDropMode` 'host': the host takes the file (the overlay's comment composers attach it as a chip). No upload
        // modal, no inline token, and the drop position is not used.
        if (handsFileDropToHost(this.fileDropMode)) {
            this.fileDrop.emit([file]);
            return;
        }
        this.pendingImageFile = file;
        this._pendingInsertPos = detail.position;
        this.showImageUploadModal = true;
        this.cdr.markForCheck();
    }

    /**
     * Clipboard IMAGE pastes always arrive as a File named "image.png" (Chromium
     * gives every paste the same generic name), so repeated pastes pile up /
     * collide in the destination folder. Give those a unique, readable name keyed
     * on the wall-clock time. Dropped files — and pasted non-image files, which
     * carry a real name from the OS — keep their filename untouched.
     */
    private _namePastedImage(file: File, source: 'paste' | 'drop'): File {
        let ext: string = 'png';
        let stamp: string = '';
        let name: string = '';
        const pad = (n: number): string => (n < 10 ? '0' + n : String(n));

        if (source !== 'paste' || !file) {
            return file;
        }
        if (!file.type || file.type.indexOf('image/') !== 0) {
            return file;
        }
        if (file.type && file.type.indexOf('/') > -1) {
            ext = file.type.split('/')[1] || 'png';
        }
        const d: Date = new Date();
        stamp = d.getFullYear() + pad(d.getMonth() + 1) + pad(d.getDate())
            + '-' + pad(d.getHours()) + pad(d.getMinutes()) + pad(d.getSeconds());
        name = 'Pasted image ' + stamp + '.' + ext;
        try {
            return new File([file], name, { type: file.type });
        } catch {
            // Some older browsers don't allow the File constructor — fall back to
            // the original; the rename-on-conflict upload still avoids the "101".
            return file;
        }
    }

    private _handleFileReject(detail: FileDropRejectDetail): void {
        let message = '';
        switch (detail.reason) {
            case 'too-large':
                message = 'File is larger than 25 MB. Please choose a smaller file.';
                break;
            case 'empty':
                message = "This file is empty and can't be uploaded.";
                break;
            default:
                message = "Couldn't add the file.";
                break;
        }
        this._toastr.error(message, null, this._toastrOptions);
    }

    public closeImageUploadModal(): void {
        this.showImageUploadModal = false;
        this.pendingImageFile = null;
        this._pendingInsertPos = null;
        this.cdr.markForCheck();
    }

    public onImageUploadComplete(result: ImageUploadResult): void {
        this.showImageUploadModal = false;
        const insertPos: number | null = this._pendingInsertPos;
        this.pendingImageFile = null;
        this._pendingInsertPos = null;

        if (result && result.success && result.copyLinkToDescription && this.editor) {
            // Same node the "/" search path inserts — an uploaded file and a
            // searched-for file are indistinguishable once in the notes.
            const ext: string = extensionFromFileName(result.fileName || '');
            const tokenAttrs: Partial<ReferenceTokenAttrs> = {
                type: 'file',
                fileKind: fileKindFromExtension(ext),
                itemId: result.fileId || '',
                driveId: result.driveId || '',
                name: result.fileName || '',
                url: result.webUrl || result.thumbnailUrl || '',
                status: 'ready',
                siteId: result.siteId || '',
                folderId: result.folderId || '',
                breadcrumb: result.folderBreadcrumb || '',
                fileSize: result.fileSize || 0,
                extension: ext
            };
            const chain = this.editor.chain().focus();
            if (insertPos !== null) {
                chain.setTextSelection(insertPos);
            }
            (chain as any).insertReferenceToken(tokenAttrs).run();
        }

        // Link the uploaded file to the task so it shows under the Files tab.
        // Done regardless of the "Copy link to description" toggle — the toggle
        // only controls inline rendering inside the editor; the file association
        // with the task is independent.
        if (result && result.success && this.taskId && this.taskId > 0) {
            this._createTaskAttachmentFromUpload(result, this.taskId);
        }

        this.imageUploaded.emit(result);
        this.cdr.markForCheck();
    }

    /**
     * Builds a CommonAttachment (entity = 22 = Task) from an OneDrive upload
     * result and saves it via the attachment service. Refreshes the task's Files
     * tab via the shared DataService subject so the new row appears immediately.
     */
    private _createTaskAttachmentFromUpload(result: ImageUploadResult, taskId: number): void {
        const knownExts: string[] = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'png'];
        const rawName: string = result.fileName || '';
        const dot: number = rawName.lastIndexOf('.');
        const ext: string = dot >= 0 ? rawName.substring(dot + 1).toLowerCase() : '';
        const fileType: string = knownExts.indexOf(ext) >= 0 ? ext : 'otherfile';

        const attachment: CommonAttachment = new CommonAttachment();
        attachment.id = 0;
        attachment.entity = 22; // Task entity type — matches edit-task-modal's linkCommentAttachmentToTask
        attachment.entityId = taskId;
        attachment.fileName = rawName;
        attachment.fileType = fileType;
        attachment.keyId = result.fileId || '';
        attachment.webLink = result.webUrl || '';
        attachment.filePath = result.webUrl || '';
        attachment.msDriveId = result.driveId || '';
        attachment.msSiteId = result.siteId || undefined;
        attachment.isShareFile = false;
        attachment.isAddToShortcut = false;

        this._attachmentService.createOrUpdateAttachment(attachment).subscribe(
            () => {
                // Trigger any subscribed host to reload its Files tab.
                this._dataService.refreshTaskAttachments.next(taskId);
            },
            () => {
                // Non-fatal — file was still uploaded + (optionally) inserted.
                this._toastr.error(
                    "File was uploaded but couldn't be linked to this task. Try refreshing.",
                    null,
                    this._toastrOptions
                );
            }
        );
    }

    /**
     * FOLDER token → open it in the Navigate-Sub-Folders modal. Dispatches via
     * the shared DataService subject AgendaComponent already listens to (same
     * flow as clicking a folder row in the Files tab). Hosts can intercept first
     * by supplying `mentionCardCallbacks.onFileOpenInFolder`.
     */
    private _openTokenFolder(attrs: ReferenceTokenAttrs): void {
        const callbacks = this.mentionCardCallbacks;
        const folderItemId: string = attrs.itemId || attrs.folderId || '';
        const item: any = {
            id: folderItemId || attrs.driveId,
            keyId: folderItemId || attrs.driveId,
            name: attrs.name || 'Files',
            extension: folderItemId ? 'folder' : 'documentLibrary',
            driveId: attrs.driveId,
            parentReference: { driveId: attrs.driveId },
            msDriveId: attrs.driveId,
            msSiteId: attrs.siteId || undefined,
            siteId: attrs.siteId || undefined,
            from: attrs.breadcrumb || 'Files'
        };

        if (callbacks && callbacks.onFileOpenInFolder) {
            callbacks.onFileOpenInFolder(tokenAttrsToMentionAttrs(attrs));
            return;
        }
        if (!attrs.driveId) {
            return;
        }
        this._dataService.openNavigationModal.next(item);
    }

    /**
     * NON-IMAGE FILE token → open the document in Office / SharePoint. Hosts
     * resolve the viewer URL via `OneDriveServiceProxy.getFileUrl()` (a raw
     * SharePoint webUrl often triggers a download instead of the viewer), so
     * prefer the host callback and only fall back to the stored URL.
     */
    private _openTokenFile(attrs: ReferenceTokenAttrs): void {
        const callbacks = this.mentionCardCallbacks;

        if (callbacks && callbacks.onFileOpenInBrowser) {
            callbacks.onFileOpenInBrowser(tokenAttrsToMentionAttrs(attrs));
            return;
        }
        if (attrs.url) {
            window.open(attrs.url, '_blank', 'noopener,noreferrer');
        }
    }

    /**
     * IMAGE FILE token → the editor's OWN lightbox, always.
     *
     * Deliberately does NOT defer to `mentionCardCallbacks.onFileClick`. That host
     * handler fetches the bytes with a hand-rolled XMLHttpRequest, which bypasses
     * Angular's HttpClient interceptor and therefore sends no Authorization header
     * — the proxy answers 401 and the preview never appears. `_openLightbox` goes
     * through HttpClient (token attached, response cached per editor), so image
     * previews work the same way on every surface that embeds this editor.
     * Non-image files, folders, tasks and clients still route to the host.
     */
    private _openTokenImage(attrs: ReferenceTokenAttrs): void {
        this._openLightbox(attrs);
    }

    /**
     * Editor-owned lightbox. Resolves the bytes through the backend
     * proxy whenever driveId + itemId are known (works across sessions and for
     * OneDrive personal, whose webUrl is a viewer page rather than raw content),
     * falling back to the stored URL otherwise.
     */
    private _openLightbox(attrs: ReferenceTokenAttrs): void {
        const fallback: string = attrs.url || '';
        const hasIds: boolean = !!(attrs.driveId && attrs.itemId);

        this.lightboxFileName = attrs.name || '';
        this.lightboxFileSize = attrs.fileSize || 0;
        // Carried so the lightbox can fall back to its "Open in new tab" card when
        // the bytes can't be fetched, instead of an empty stage.
        this.lightboxExtension = attrs.extension || '';
        this.lightboxWebUrl = fallback;
        // Show the modal immediately even before the URL resolves so the user gets
        // an instant response; `lightboxLoading` drives the spinner until it does.
        this.lightboxImageUrl = '';
        this.lightboxLoading = hasIds;
        this.showLightbox = true;
        this.cdr.markForCheck();

        if (hasIds) {
            this._resolveImageUrl(attrs.driveId, attrs.itemId).then(
                (url) => this.ngZone.run(() => {
                    if (!this.showLightbox) {
                        return;
                    }
                    this.lightboxLoading = false;
                    this.lightboxImageUrl = url;
                    this.cdr.markForCheck();
                }),
                () => this.ngZone.run(() => {
                    if (!this.showLightbox) {
                        return;
                    }
                    // Clearing `loading` is what lets the lightbox settle on either
                    // the image or its "Preview isn't available · Open in new tab"
                    // card. Leaving it set would hang on the spinner forever.
                    this.lightboxLoading = false;
                    this.lightboxImageUrl = fallback;
                    this.cdr.markForCheck();
                })
            );
        } else {
            this.lightboxLoading = false;
            this.lightboxImageUrl = fallback;
            this.cdr.markForCheck();
        }
    }

    public closeLightbox(): void {
        this.showLightbox = false;
        this.lightboxImageUrl = '';
        this.lightboxFileName = '';
        this.lightboxFileSize = 0;
        this.lightboxExtension = '';
        this.lightboxWebUrl = '';
        this.lightboxLoading = false;
        this.cdr.markForCheck();
    }

    /**
     * Routes a mention-chip click to the host-supplied callback. Done via
     * a single dispatcher so the chip-click handlers in the node-view stay
     * tiny and the same wiring works for embedded hosts (edit-task,
     * add-task) without duplicating click logic.
     */
    private _invokeMentionCallback(
        kind: 'task' | 'client',
        attrs: MentionCardAttrs
    ): void {
        const callbacks = this.mentionCardCallbacks;
        if (!callbacks) {
            return;
        }
        if (kind === 'task' && callbacks.onTaskClick) {
            callbacks.onTaskClick(attrs);
            return;
        }
        if (kind === 'client' && callbacks.onClientClick) {
            callbacks.onClientClick(attrs);
            return;
        }
    }
}
